import React from 'react';
import { slide as Menu } from 'react-burger-menu';
import ReactTooltip from 'react-tooltip';
import ReactDOMClient from 'react-dom/client';

import './index.scss';

// ====================================================================

class Battleground {
  constructor(width, height, adjustCoordinates, imageName, updateHighResolutionMinimapCanvas, drawCanvas, updateMinimapDimensions, phasesDefaultValues, warDefaultShrink, specialNormalModes) {
    this.width = width;
    this.height = height;
    this.minimumLength = Math.min(width, height);
    this.adjustCoordinates = adjustCoordinates;
    this.radius = 0.5 * Math.sqrt(this.width ** 2 + this.height ** 2) + 50.0;
    this.minimap = new Minimap(imageName, [8, 8], 2, updateHighResolutionMinimapCanvas, drawCanvas, updateMinimapDimensions);
    this.normalizedRadius = this.radius / this.width;
    this.phasesDefaultValues = phasesDefaultValues;
    this.warDefaultShrink = warDefaultShrink;
    this.specialNormalModes = specialNormalModes;
  }
}

// --------------------------------------------------------------------

class ColorLineCanvas extends React.Component {
  constructor(props) {
    super(props);

    this.colorLineRef = React.createRef();
  }

  componentDidMount() {
    this.drawLine();
  }

  componentDidUpdate(prevProps) {
    this.drawLine();
  }

  drawLine() {
    const colorLineContext = this.colorLineRef.current.getContext('2d', { alpha: false });

    colorLineContext.fillStyle = this.props.backgroundColor;
    colorLineContext.fillRect (0, 0, this.props.width, this.props.height);

    colorLineContext.lineWidth = 2.0;

    if (this.props.lineColorRGBs.length === 1) {
      colorLineContext.beginPath();

      const strokeHeight = 2 * this.props.height / 3;

      colorLineContext.moveTo(0, strokeHeight);

      colorLineContext.lineTo(this.props.width, strokeHeight);

      colorLineContext.setLineDash(this.props.lineDashes[0]);
      colorLineContext.strokeStyle = 'rgba(' + this.props.lineColorRGBs[0] + ', 1.0)';

      colorLineContext.stroke();
    } else if (this.props.lineColorRGBs.length === 2) {
      colorLineContext.beginPath();

      let strokeHeight = 0.5 * this.props.height;

      colorLineContext.moveTo(0, strokeHeight);

      colorLineContext.lineTo(this.props.width, strokeHeight);

      colorLineContext.setLineDash(this.props.lineDashes[0]);
      colorLineContext.strokeStyle = 'rgba(' + this.props.lineColorRGBs[0] + ', 1.0)';

      colorLineContext.stroke();

      colorLineContext.beginPath();

      strokeHeight = 5 * this.props.height / 6;

      colorLineContext.moveTo(0, strokeHeight);

      colorLineContext.lineTo(this.props.width, strokeHeight);

      colorLineContext.setLineDash(this.props.lineDashes[1]);
      colorLineContext.strokeStyle = 'rgba(' + this.props.lineColorRGBs[1] + ', 1.0)';

      colorLineContext.stroke();
    } else if (this.props.lineColorRGBs.length === 3) {
      colorLineContext.beginPath();

      let strokeHeight = 5 * this.props.height / 12;

      colorLineContext.moveTo(0, strokeHeight);

      colorLineContext.lineTo(this.props.width, strokeHeight);

      colorLineContext.setLineDash(this.props.lineDashes[0]);
      colorLineContext.strokeStyle = 'rgba(' + this.props.lineColorRGBs[0] + ', 1.0)';

      colorLineContext.stroke();

      colorLineContext.beginPath();

      strokeHeight = 2 * this.props.height / 3;

      colorLineContext.moveTo(0, strokeHeight);

      colorLineContext.lineTo(this.props.width, strokeHeight);

      colorLineContext.setLineDash(this.props.lineDashes[1]);
      colorLineContext.strokeStyle = 'rgba(' + this.props.lineColorRGBs[1] + ', 1.0)';

      colorLineContext.stroke();

      colorLineContext.beginPath();

      strokeHeight = 11 * this.props.height / 12;

      colorLineContext.moveTo(0, strokeHeight);

      colorLineContext.lineTo(this.props.width, strokeHeight);

      colorLineContext.setLineDash(this.props.lineDashes[2]);
      colorLineContext.strokeStyle = 'rgba(' + this.props.lineColorRGBs[2] + ', 1.0)';

      colorLineContext.stroke();
    }
  }

  render() {
    return (
      <canvas width={this.props.width} height={this.props.height} ref={this.colorLineRef} />
    );
  }
}

// --------------------------------------------------------------------

class InterpolationRange {
  constructor(inVal, outVal) {
    this.endCoordinate = inVal;
    this.endValue = outVal;
  }
}

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

class CubicSplineRange extends InterpolationRange {
  constructor (leaveSlope, arriveSlope, inVal, outVal) {
    super(inVal, outVal);

    this.startSlope = leaveSlope;
    this.endSlope = arriveSlope;
  }

  getValue(previousRange, coordinate) {
    const t = (coordinate - previousRange.endCoordinate) / (this.endCoordinate - previousRange.endCoordinate);

    return (2.0 * t ** 3 - 3.0 * t ** 2 + 1.0) * previousRange.endValue +
           (t ** 3 - 2.0 * t ** 2 + t) * this.startSlope +
           (t ** 3 - t ** 2) * this.endSlope +
           (-2.0 * t ** 3 + 3.0 * t ** 2) * this.endValue;
  }
}

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

class LinearRange extends InterpolationRange {
  getValue(previousRange, coordinate) {
    const t = (coordinate - previousRange.endCoordinate) / (this.endCoordinate - previousRange.endCoordinate);

    return (1.0 - t) * previousRange.endValue +
           t * this.endValue;
  }
}

// --------------------------------------------------------------------

class InterpolationTable {
  constructor(interpolationRanges) {
    this.interpolationRanges = interpolationRanges;
  }

  getValue(coordinate) {
    if (coordinate <= this.interpolationRanges[0].endCoordinate) {
      return this.interpolationRanges[0].endValue;
    } else if (coordinate >= this.interpolationRanges[this.interpolationRanges.length-1].endCoordinate) {
      return this.interpolationRanges[this.interpolationRanges.length-1].endValue;
    }

    for (let i = 1; i < this.interpolationRanges.length; i++) {
      if (coordinate < this.interpolationRanges[i].endCoordinate) {
        return this.interpolationRanges[i].getValue(this.interpolationRanges[i-1], coordinate);
      }
    }
  }

  maximumCoordinate() {
    return this.interpolationRanges[this.interpolationRanges.length-1].endCoordinate;
  }

  minimumCoordinate() {
    return this.interpolationRanges[0].endCoordinate;
  }
}

// --------------------------------------------------------------------

class Minimap {
  constructor(imageName, divisions, queueLength, updateHighResolutionMinimapCanvas, drawCanvas, updateMinimapDimensions) {
    this.imageName = imageName;

    this.lowResolutionImage = new MinimapLowResolutionImage();
    this.lowResolutionImage.image.onload = () => { this.imageOnload(this.lowResolutionImage); };

    this.divisions = divisions;

    this.divisionFractions = [1.0 / this.divisions[0], 1.0 / this.divisions[1]];

    let middleIndex = [0.5 * this.divisions[0], 0.5 * this.divisions[1]];

    let maxDistance = middleIndex[0] + middleIndex[1];

    this.highResolutionDimensions = [];

    this.highResolutionImages = [];
    let key = 0;
    for (let distance = 0; distance <= maxDistance; distance++) {
      for (let i = 0; i < middleIndex[0]; i++) {
        for (let j = 0; j < middleIndex[1]; j++) {
          let combinedDistance = (middleIndex[0] - i - 1) + (middleIndex[1] - j - 1);

          if (combinedDistance === distance) {
            this.highResolutionImages.push(new MinimapHighResolutionImage(key, [i, j]));

            key++;

            let ii = 2 * middleIndex[0] - i - 1;

            this.highResolutionImages.push(new MinimapHighResolutionImage(key, [ii, j]));

            key++;

            let jj = 2 * middleIndex[1] - j - 1;

            this.highResolutionImages.push(new MinimapHighResolutionImage(key, [i, jj]));

            key++;

            this.highResolutionImages.push(new MinimapHighResolutionImage(key, [ii, jj]));

            key++;
          }
        }
      }
    }
    for (let i = 0; i < this.highResolutionImages.length; i++) {
      this.highResolutionImages[i].image.onload = () => { this.imageOnload(this.highResolutionImages[i]); };
    }

    this.anyHighResolutionImageLoading = false;
    this.anyHighResolutionImageLoaded = false;
    this.allHighResolutionImagesLoaded = false;

    this.queueLength = queueLength;
    this.maxPriority = 0;

    this.loadingImages = false;

    this.updateHighResolutionMinimapCanvas = updateHighResolutionMinimapCanvas;

    this.drawCanvas = drawCanvas;

    this.updateMinimapDimensions = updateMinimapDimensions;
  }

  loadedImages() {
    return this.lowResolutionImage.loaded || this.allHighResolutionImagesLoaded;
  }

  loadImages() {
    if ((!this.lowResolutionImage.loading) && (!this.lowResolutionImage.loaded)) {
      this.lowResolutionImage.loading = true;
      this.lowResolutionImage.image.src = this.imageName + "_Low_Res.webp";

      this.loadingImages = true;
    } else {
      if (this.anyHighResolutionImageLoaded) { this.updateHighResolutionMinimapCanvas(); }

      let queueLength = this.queueLength;

      if ((queueLength > 0) && !this.allHighResolutionImagesLoaded) {
        priority_loop: for (let priority = this.maxPriority; priority >= 0; priority--) {
          for (let i = 0; i < this.highResolutionImages.length; i++) {
            if (!this.highResolutionImages[i].loading && !this.highResolutionImages[i].loaded && (this.highResolutionImages[i].priority === priority)) {
              this.highResolutionImages[i].loading = true;
              this.highResolutionImages[i].image.src = this.imageName + "_High_Res_" + this.highResolutionImages[i].indexes[0] + "_" + this.highResolutionImages[i].indexes[1] + ".webp";

              queueLength--;

              if (queueLength === 0) { break priority_loop; }
            }
          }
        }
      }

      if (queueLength < this.queueLength) { this.loadingImages = true; }
    }
  }

  imageOnload(image) {
    image.loaded = true;
    image.loading = false;

    if (!this.loadingImages) { return; }

    if (image.type() === "MinimapLowResolutionImage") {
      this.anyHighResolutionImageLoading = true;

      let queueLength = this.queueLength;

      if (queueLength > 0) {
        priority_loop: for (let priority = this.maxPriority; priority >= 0; priority--) {
          for (let i = 0; i < this.highResolutionImages.length; i++) {
            if (!this.highResolutionImages[i].loading && !this.highResolutionImages[i].loaded && (this.highResolutionImages[i].priority === priority)) {
              this.highResolutionImages[i].loading = true;
              this.highResolutionImages[i].image.src = this.imageName + "_High_Res_" + this.highResolutionImages[i].indexes[0] + "_" + this.highResolutionImages[i].indexes[1] + ".webp";

              queueLength--;

              if (queueLength === 0) { break priority_loop; }
            }
          }
        }
      }
//  } else if (image.type() === "MinimapHighResolutionImage") {
    } else {
      if (!this.anyHighResolutionImageLoaded) {
        this.highResolutionDimensions = [this.divisions[0] * image.image.naturalWidth, this.divisions[1] * image.image.naturalHeight];
      }

      this.anyHighResolutionImageLoaded = true;

      let allHighResolutionImagesLoaded = true;

      for (let i = 0; i < this.highResolutionImages.length; i++) {
        if (!this.highResolutionImages[i].loaded) {
          allHighResolutionImagesLoaded = false;

          break;
        }
      }

      this.allHighResolutionImagesLoaded = allHighResolutionImagesLoaded;

      if (allHighResolutionImagesLoaded) {
        this.anyHighResolutionImageLoading = false;

        this.loadingImages = false;
      } else {
        priority_loop: for (let priority = this.maxPriority; priority >= 0; priority--) {
          for (let i = 0; i < this.highResolutionImages.length; i++) {
            if (!this.highResolutionImages[i].loading && !this.highResolutionImages[i].loaded && (this.highResolutionImages[i].priority === priority)) {
              this.highResolutionImages[i].loading = true;
              this.highResolutionImages[i].image.src = this.imageName + "_High_Res_" + this.highResolutionImages[i].indexes[0] + "_" + this.highResolutionImages[i].indexes[1] + ".webp";

              break priority_loop;
            }
          }
        }
      }

      this.updateHighResolutionMinimapCanvas();
    }

    if (!this.updateMinimapDimensions()) {
      this.drawCanvas(true);
    }
  }
}

// --------------------------------------------------------------------

class MinimapImage {
  constructor() {
    this.image = new Image();
    this.loading = false;
    this.loaded = false;
  }

  type() { return "MinimapImage"; }
}

// --------------------------------------------------------------------

class MinimapHighResolutionImage extends MinimapImage {
  constructor(key, indexes) {
    super();
    this.key = key;
    this.indexes = indexes;
    this.priority = 0;
  }

  type() { return "MinimapHighResolutionImage"; }
}

// --------------------------------------------------------------------

class MinimapLowResolutionImage extends MinimapImage {
  type() { return "MinimapLowResolutionImage"; }
}

// --------------------------------------------------------------------

class MovingTransitSchedule extends React.Component {
  constructor(props) {
    super(props);
    this.battleground = React.createRef();
    this.battlegrounds = [];
    this.battlegroundRails = [];
    this.battlegroundRailStations = [];
    this.clientMouseX = 0;
    this.clientMouseY = 0;
    this.credits = React.createRef();
    this.highResolutionMinimapCanvas = React.createRef();
    this.isTouchDevice = false;
    this.maximumMinimapScale = 16.0;
    this.minimapCanvas = React.createRef();
    this.minimapOffsetX = 0;
    this.minimapOffsetY = 0;
    this.minimapMouseHover = false;
    this.minimapMouseX = 0;
    this.minimapMouseY = 0;
    this.minimapPadding = React.createRef();
    this.minimapScale = 1.0;
    this.minimapSpacer = React.createRef();
    this.mounted = false;
    this.mouseDown = false;
    this.mouseDownShiftKey = false;
    this.mouseDownMinimapOffsetX = 0;
    this.mouseDownMinimapOffsetY = 0;
    this.mouseDownMinimapMouseX = 0;
    this.mouseDownMinimapMouseY = 0;
    this.mouseMoveCoordinateX = 0;
    this.mouseMoveCoordinateY = 0;
    this.phases = [React.createRef(),
                   React.createRef(),
                   React.createRef(),
                   React.createRef(),
                   React.createRef(),
                   React.createRef(),
                   React.createRef(),
                   React.createRef(),
                   React.createRef()];
    this.phasesTicks = [];
    this.phasesValues = React.createRef();
    this.pinchPreviousMinimapCenterX = 0;
    this.pinchPreviousMinimapCenterY = 0;
    this.pinchStartMinimapDistance = 0;
    this.pinchStartMinimapScale = 1.0;
    this.playSpeed = 1.0;
    this.playSpeedSelect = React.createRef();
    this.playing = false;
    this.playIntervalID = null;
    this.playIntervalMilliseconds = 1000 / 24.0;
    this.rails = [];
    this.railStations = [];
    this.settings = React.createRef();
    this.time = React.createRef();
    this.timeSeconds = 0.0;
    this.title = React.createRef();
    this.tooltipLines = [];
    this.totalRoundTimeSeconds = 0;
    this.totalRoundTimeSecondsInverse = 0.0;
    this.touchRecentlyEnded = false;
    this.touchRecentlyEndedTimeoutID = undefined;
    this.touchRecentlyMoved = false;
    this.touchRecentlyMovedTimeoutID = undefined;
    this.touchStartMinimapOffsetX = 0;
    this.touchStartMinimapOffsetY = 0;
    this.touchStartMinimapTouchX = 0;
    this.touchStartMinimapTouchY = 0;
    this.userAgentIsSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    this.wheelTimeoutID = undefined;
  }

  battlegroundMaximumMinimapScale(battlegroundName) {
    if (isNaN(this.battlegrounds[battlegroundName].minimumLength)) { return 16.0; }

    let maximumMinimapScale = 4.0;

    while (this.battlegrounds[battlegroundName].minimumLength > 127.5 * maximumMinimapScale) {
      maximumMinimapScale += this.minimapScaleIncrement(maximumMinimapScale);
    }

    return maximumMinimapScale;
  }

  clearTouchRecentlyEndedTimeout(movingTrainSchedule) {
    movingTrainSchedule.touchRecentlyEnded = false;
  }

  clearTouchRecentlyMovedTimeout(movingTrainSchedule) {
    movingTrainSchedule.touchRecentlyMoved = false;
  }

  componentDidMount() {
    if (('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || (navigator.msMaxTouchPoints > 0)) {
      this.isTouchDevice = true;

      const lmts = document.getElementById('lmts');

      lmts.style.fontSize = "20px";
    }

    this.battlegrounds["Erangel"] = new Battleground(8127.75, 8127.75,
                                                     [0, 0],
                                                     "/minimaps/mts/Erangel_Minimap_Stations",
                                                     this.updateHighResolutionMinimapCanvas.bind(this),
                                                     this.drawCanvas.bind(this),
                                                     this.updateMinimapDimensions.bind(this),
                                                     [new PhaseDefaultValues(90, 240, 270, 0.4, 0.35, 0.5, 0),
                                                      new PhaseDefaultValues(0, 120, 90, 0.6, 0.55, 0.56, 0),
                                                      new PhaseDefaultValues(0, 100, 80, 0.8, 0.6, 0.56, 0),
                                                      new PhaseDefaultValues(0, 100, 80, 1, 0.55, 0.5, 0),
                                                      new PhaseDefaultValues(0, 100, 60, 3, 0.5, 0.5, 0),
                                                      new PhaseDefaultValues(0, 90, 30, 5, 0.5, 0.5, 0),
                                                      new PhaseDefaultValues(0, 70, 30, 7, 0.5, 0.5, 0),
                                                      new PhaseDefaultValues(0, 60, 30, 9, 0.5, 0.5, 0),
                                                      new PhaseDefaultValues(30, 30, 30, 11, 0.001, 10, 0)],
                                                     null,
                                                     []);
    this.battlegrounds["Vikendi_20.2"] = new Battleground(6121, 6121,
                                                          [0, 0],
                                                          "/minimaps/mts/Vikendi_20.2_Minimap_Stations",
                                                          this.updateHighResolutionMinimapCanvas.bind(this),
                                                          this.drawCanvas.bind(this),
                                                          this.updateMinimapDimensions.bind(this),
                                                          [new PhaseDefaultValues(90, 240, 300, 0.4, 0.4, 0.5, 0),
                                                           new PhaseDefaultValues(0, 120, 120, 0.6, 0.65, 0.5, 0),
                                                           new PhaseDefaultValues(0, 90, 90, 0.8, 0.62, 0.5, 0),
                                                           new PhaseDefaultValues(0, 80, 90, 1, 0.6, 0.5, 0),
                                                           new PhaseDefaultValues(0, 80, 70, 3, 0.55, 0.5, 0),
                                                           new PhaseDefaultValues(0, 60, 60, 5, 0.5, 0.5, 0),
                                                           new PhaseDefaultValues(0, 60, 60, 7, 0.5, 0.5, 0),
                                                           new PhaseDefaultValues(0, 60, 60, 9, 0.5, 0.5, 0),
                                                           new PhaseDefaultValues(30, 30, 30, 14, 0.001, 1, 0)],
                                                          null,
                                                          []);
    this.battlegrounds["Vikendi"] = new Battleground(8160, 8160,
                                                     [0, 0],
                                                     "/minimaps/mts/Vikendi_Minimap_Stations",
                                                     this.updateHighResolutionMinimapCanvas.bind(this),
                                                     this.drawCanvas.bind(this),
                                                     this.updateMinimapDimensions.bind(this),
                                                     [new PhaseDefaultValues(90, 240, 270, 0.4, 0.35, 0.5, 0),
                                                      new PhaseDefaultValues(0, 120, 90, 0.6, 0.55, 0.56, 0),
                                                      new PhaseDefaultValues(0, 100, 80, 0.8, 0.6, 0.56, 0),
                                                      new PhaseDefaultValues(0, 100, 80, 1, 0.55, 0.5, 0),
                                                      new PhaseDefaultValues(0, 100, 60, 3, 0.5, 0.5, 0),
                                                      new PhaseDefaultValues(0, 90, 30, 5, 0.5, 0.5, 0),
                                                      new PhaseDefaultValues(0, 70, 30, 7, 0.5, 0.5, 0),
                                                      new PhaseDefaultValues(0, 60, 30, 9, 0.5, 0.5, 0),
                                                      new PhaseDefaultValues(30, 30, 30, 11, 0.001, 10, 0)],
                                                     null,
                                                     []);

    const search = window.location.search;
    const searchParameters = new URLSearchParams(search);

    if (this.battleground.current != null) {
      let searchParameter = searchParameters.get("m");

      if (searchParameter != null) {
        if ((searchParameter === "Erangel") ||
            (searchParameter === "Vikendi_20.2") ||
            (searchParameter === "Vikendi")) {
          this.battleground.current.value = searchParameter;
        } else if ((searchParameter === "FantasyBR") ||
                   (searchParameter === "HalloweenBR")) {
          this.battleground.current.value = "Erangel";
        } else if (/Vikendi_[0-9.]+/.test(searchParameter)) {
          this.battleground.current.value = "Vikendi";
        }
      }

      this.maximumMinimapScale = this.battlegroundMaximumMinimapScale(this.battleground.current.value);
    }

    let minimapScale = this.minimapScale;

    { let searchParameter = searchParameters.get("ms");

      if (searchParameter != null) {
        if (!isNaN(searchParameter)) {
          minimapScale = Number(searchParameter);
        }
    } }

    let minimapOffsetX = this.minimapOffsetX;

    { let searchParameter = searchParameters.get("mx");

      if (searchParameter != null) {
        if (!isNaN(searchParameter)) {
          minimapOffsetX = -Number(searchParameter) * minimapScale * this.minimapCanvas.current.width + 0.5 * this.minimapCanvas.current.width;
        }
    } }

    let minimapOffsetY = this.minimapOffsetY;

    { let searchParameter = searchParameters.get("my");

      if (searchParameter != null) {
        if (!isNaN(searchParameter)) {
          minimapOffsetY = -Number(searchParameter) * minimapScale * this.minimapCanvas.current.height + 0.5 * this.minimapCanvas.current.height;
        }
    } }

    this.setMinimapViewport(minimapScale, minimapOffsetX, minimapOffsetY);

    this.selectBattleground();

    this.setPhaseValuesForAllPhases();

    this.fetchTransitData();

    this.setRailInformationTableHeaderWidth();

    this.updateMinimapDimensions();

    window.addEventListener('resize', this.handleResize);

    document.addEventListener('mousedown', this.handleMouseDown);
    document.addEventListener('mousemove', this.handleMouseMove);
    document.addEventListener('mouseup', this.handleMouseUp);

    this.minimapCanvas.current.addEventListener('wheel', this.scaleMinimap, { passive: false });

    this.mounted = true;
  }

  constructRailStationsSchedule(battlegroundName) {
    let totalRoundTimeSeconds = 0;

    for (let i = 0; i < 9; i++) {
      totalRoundTimeSeconds += this.battlegrounds[battlegroundName].phasesDefaultValues[i].delay +
                               this.battlegrounds[battlegroundName].phasesDefaultValues[i].wait +
                               this.battlegrounds[battlegroundName].phasesDefaultValues[i].move;
    }

    this.totalRoundTimeSeconds = totalRoundTimeSeconds;

    for (let i = 0; i < this.rails.length; i++) {
      if (this.rails[i].battleground !== battlegroundName) { continue; }

      for (let j = 0; j < this.rails[i].railVehicles.length; j++) {
        let railVehicleTime = 0;

        let railVehicleReverse = false;

        let railStationIndex = this.getRailStationIndex(battlegroundName, this.rails[i].railStations[this.rails[i].railVehicles[j].startingIndex]);

        let railVehicleArrivalTime = 0;

        this.railStations[railStationIndex].arrivals.push(new RailStationArrival(this.convertSecondsToPhaseNumber(battlegroundName, Math.round(railVehicleArrivalTime)), railVehicleArrivalTime, this.rails[i].colorRGB, this.rails[i].lineDash));

        while (railVehicleArrivalTime < this.totalRoundTimeSeconds) {
          railVehicleReverse = false;

          for (let k = 0; k < (2 * this.rails[i].railNodes.length); ++k) {
            let kk = this.rails[i].railVehicles[j].startingIndex + k;

            if (kk >= this.rails[i].railNodes.length) {
              kk = kk % this.rails[i].railNodes.length;

              if (!this.rails[i].railVehicles[j].loop) {
                kk = this.rails[i].railNodes.length - 1 - kk;

                if (kk >= 0) {
                  railVehicleReverse = true;
                } else {
                  kk = -(kk + 1);

                  railVehicleReverse = false;
                }
              }
            }

            railVehicleArrivalTime = railVehicleTime + this.rails[i].railNodes[kk].arrivalTime;

            if (railVehicleArrivalTime > this.totalRoundTimeSeconds) { break; }

            if (railVehicleReverse !== this.rails[i].railNodes[kk].inverseMove) {
              if (this.rails[i].railNodes[kk].inverseMove) {
                railStationIndex = kk + 1;
              } else {
                railStationIndex = kk;
              }
            } else {
              if (this.rails[i].railVehicles[j].loop) {
                railStationIndex = (kk + 1) % this.rails[i].railNodes.length;
              } else if (this.rails[i].railNodes[kk].inverseMove) {
                railStationIndex = kk;
              } else {
                railStationIndex = kk + 1;
              }
            }

            railStationIndex = this.getRailStationIndex(battlegroundName, this.rails[i].railStations[railStationIndex]);

            this.railStations[railStationIndex].arrivals.push(new RailStationArrival(this.convertSecondsToPhaseNumber(battlegroundName, Math.round(railVehicleArrivalTime)), railVehicleArrivalTime, this.rails[i].colorRGB, this.rails[i].lineDash));

            railVehicleTime += this.rails[i].railNodes[kk].duration;
          }
        }
      }
    }

    for (let i = 0; i < this.railStations.length; i++) {
      if (this.railStations[i].battleground !== battlegroundName) { continue; }

      this.railStations[i].arrivals.sort(function(a, b) { return a.time - b.time; });
    }
  }

  convertMapToCanvasCoordinateX(x) {
    return (x * this.minimapScale * this.minimapCanvas.current.width + this.minimapOffsetX);
  }

  convertMapToCanvasCoordinateY(y) {
    return (y * this.minimapScale * this.minimapCanvas.current.height + this.minimapOffsetY);
  }

  convertSecondsToMinutesAndSecondsString(seconds) {
    let stringSeconds = seconds;

    let stringMinutes = Math.floor(stringSeconds / 60);

    stringSeconds -= 60 * stringMinutes;

    if (stringMinutes === 0) {
      return stringSeconds + "s";
    } else {
      return stringMinutes + "m" + stringSeconds + "s";
    }
  }

  convertSecondsToPhaseNumber(battlegroundName, seconds) {
    if (battlegroundName === "") { return "0"; }

    let phaseSeconds = Number(seconds);

    let phaseNumber = 9;
    let phaseDuration;
    for (let i = 0; i < 8; i++) {
      phaseDuration = this.battlegrounds[battlegroundName].phasesDefaultValues[i].delay +
                      this.battlegrounds[battlegroundName].phasesDefaultValues[i].wait +
                      this.battlegrounds[battlegroundName].phasesDefaultValues[i].move;

      if (phaseDuration > phaseSeconds) {
        phaseNumber = i + 1;

        break;
      }

      phaseSeconds -= phaseDuration;
    }

    return phaseNumber;
  }

  convertSecondsToPhaseString(battlegroundName, seconds) {
    if (battlegroundName === "") { return "-1s"; }

    let phaseSeconds = Number(seconds);

    let phaseDuration;
    for (let i = 0; i < 8; i++) {
      phaseDuration = this.battlegrounds[battlegroundName].phasesDefaultValues[i].delay +
                      this.battlegrounds[battlegroundName].phasesDefaultValues[i].wait +
                      this.battlegrounds[battlegroundName].phasesDefaultValues[i].move;

      if (phaseDuration > phaseSeconds) { break; }

      phaseSeconds -= phaseDuration;
    }

    return this.convertSecondsToMinutesAndSecondsString(phaseSeconds);
  }

  convertSecondsToPhaseStrings(battlegroundName, seconds) {
    if (battlegroundName === "") { return "Phase 0 -1s"; }

    let phaseSeconds = Number(seconds);

    let phaseNumber = 9;
    let phaseDuration;
    for (let i = 0; i < 8; i++) {
      phaseDuration = this.battlegrounds[battlegroundName].phasesDefaultValues[i].delay +
                      this.battlegrounds[battlegroundName].phasesDefaultValues[i].wait +
                      this.battlegrounds[battlegroundName].phasesDefaultValues[i].move;

      if (phaseDuration > phaseSeconds) {
        phaseNumber = i + 1;

        break;
      }

      phaseSeconds -= phaseDuration;
    }

    const phaseElapsedString = "Phase " + phaseNumber + " +" + this.convertSecondsToMinutesAndSecondsString(phaseSeconds);

    let phaseName;

    if (phaseNumber < 9) {
      phaseName = "Phase " + (phaseNumber + 1);
    } else {
      phaseName = "Round End";
    }

    const i = phaseNumber - 1;

    phaseDuration = this.battlegrounds[battlegroundName].phasesDefaultValues[i].delay +
                    this.battlegrounds[battlegroundName].phasesDefaultValues[i].wait +
                    this.battlegrounds[battlegroundName].phasesDefaultValues[i].move;

    phaseSeconds = Math.abs(phaseSeconds - phaseDuration);

    const phaseRemainingString = phaseName + " -" + this.convertSecondsToMinutesAndSecondsString(phaseSeconds);

    return [phaseElapsedString, phaseRemainingString];
  }

  drawCanvas(renderHTML) {
    if (this.battleground.current == null) { return; }

    if (this.minimapCanvas.current == null) { return; }

    const context = this.minimapCanvas.current.getContext('2d', { alpha: false });

    if (this.battlegrounds[this.battleground.current.value].minimap.loadedImages()) {
      context.imageSmoothingEnabled = false;

      const scaleX = this.minimapScale * this.minimapCanvas.current.width;
      const scaleY = this.minimapScale * this.minimapCanvas.current.height;
      const scale = Math.min(scaleX, scaleY);

      const matrix = new DOMMatrix([scaleX, 0.0, 0.0, scaleY,
                                    this.minimapOffsetX, this.minimapOffsetY]);

      context.setTransform(matrix);

      context.clearRect(0, 0, 1.0, 1.0);

      this.drawMinimap();

      context.setTransform();

      for (let i = 0; i < this.battlegroundRails.length; i++) {
        this.battlegroundRails[i].setRailPath(matrix, scale, this.battlegrounds[this.battleground.current.value].minimumLength);

        context.lineWidth = Math.max(2.0, scale * 6.0 / this.battlegrounds[this.battleground.current.value].minimumLength);
        let lineDash = [];
        for (let j = 0; j < this.battlegroundRails[i].lineDash.length; j++) {
          lineDash.push(scale * this.battlegroundRails[i].lineDash[j] / 1024.0);
        }
        context.setLineDash(lineDash);
        context.strokeStyle = 'rgba(' + this.battlegroundRails[i].colorRGB + ', 0.8)';

        context.stroke(this.battlegroundRails[i].railPath);

        context.lineWidth = 1.5 * context.lineWidth;
        context.setLineDash([]);
        context.strokeStyle = 'rgba(' + this.battlegroundRails[i].colorRGB + ', 1.0)';

        context.stroke(this.battlegroundRails[i].railStationsPath);
      }

      context.save();

      let clipPath = new Path2D();

      for (let i = 0; i < this.battlegroundRails.length; i++) {
        this.battlegroundRails[i].setRailVehiclesPaths(matrix, scale, this.battlegrounds[this.battleground.current.value].minimumLength, this.timeSeconds);

        context.lineWidth = 2.0 * Math.max(2.0, scale * 6.0 / this.battlegrounds[this.battleground.current.value].minimumLength);
        context.strokeStyle = 'rgba(' + this.battlegroundRails[i].colorRGB + ', 1.0)';

        context.stroke(this.battlegroundRails[i].railVehiclesClipPath);

        clipPath.addPath(this.battlegroundRails[i].railVehiclesClipPath);
      }

      context.clip(clipPath);

      context.setTransform(matrix);

      this.drawMinimap();

      context.setTransform();

      for (let i = 0; i < this.battlegroundRails.length; i++) {
        context.fillStyle = 'rgba(' + this.battlegroundRails[i].colorRGB + ', 0.8)';

        context.fill(this.battlegroundRails[i].railVehiclesPath);

        context.lineWidth = Math.max(0.25, scale * 0.75 / this.battlegrounds[this.battleground.current.value].minimumLength);
        context.strokeStyle = 'rgba(' + this.battlegroundRails[i].colorRGB + ', 1.0)';

        context.stroke(this.battlegroundRails[i].railVehiclesPath);
      }

      context.restore();
    } else {
      context.fillStyle = '#535355';
      context.fillRect (0, 0, this.minimapCanvas.current.width, this.minimapCanvas.current.height);
    }

    if (renderHTML) { this.setState({}); }
  }

  drawMinimap() {
    if (this.minimapCanvas.current == null) { return; }

    if (this.battleground.current == null) { return; }

    const minimap = this.battlegrounds[this.battleground.current.value].minimap;

    const context = this.minimapCanvas.current.getContext('2d', { alpha: false });

    if ((!(minimap.allHighResolutionImagesLoaded && (this.highResolutionMinimapCanvas.current != null)) || this.userAgentIsSafari) && minimap.lowResolutionImage.loaded) {
      context.drawImage(minimap.lowResolutionImage.image, 0.0, 0.0, 1.0, 1.0);
    }

    if (minimap.anyHighResolutionImageLoaded && (this.highResolutionMinimapCanvas.current != null)) {
      if (this.userAgentIsSafari) {
        for (let i = 0; i < minimap.highResolutionImages.length; i++) {
          if (minimap.highResolutionImages[i].loaded) {
            const highResolutionImageOffsetX = minimap.highResolutionImages[i].indexes[0] * minimap.divisionFractions[0];
            const highResolutionImageOffsetY = minimap.highResolutionImages[i].indexes[1] * minimap.divisionFractions[1];

            context.drawImage(minimap.highResolutionImages[i].image, highResolutionImageOffsetX, highResolutionImageOffsetY, minimap.divisionFractions[0], minimap.divisionFractions[1]);
          }
        }
      } else {
        context.drawImage(this.highResolutionMinimapCanvas.current, 0.0, 0.0, 1.0, 1.0);
      }
    }
  }

  fetchTransitData() {
    fetch('/data/rails_erangel.min.json')
      .then(response => response.json())
      .then(transitData => this.parseTransitData("Erangel", transitData));

    fetch('/data/rails_vikendi_20.2.min.json')
      .then(response => response.json())
      .then(transitData => this.parseTransitData("Vikendi_20.2", transitData));

    fetch('/data/rails_vikendi.min.json')
      .then(response => response.json())
      .then(transitData => this.parseTransitData("Vikendi", transitData));
  }

  getRailStationIndex(battlegroundName, railStationName) {
    for (let i = 0; i < this.railStations.length; i++) {
      if (this.railStations[i].battleground !== battlegroundName) { continue; }

      if (this.railStations[i].name === railStationName) {
        return i;
      }
    }

    return -1;
  }

  haltPlayInterval = (event) => {
    if (this.playIntervalID != null) {
      clearInterval(this.playIntervalID);
      this.playIntervalID = null;
    }

    if (this.time.current != null) {
      this.timeSeconds = Number(this.time.current.value);
    }

    this.drawCanvas(true);
  }

  handleMouseDown = (event) => {
    if (this.minimapMouseHover) {
      event.stopPropagation();
      event.preventDefault();

      this.mouseDownMinimapMouseX = this.minimapMouseX;
      this.mouseDownMinimapMouseY = this.minimapMouseY;

      this.mouseDownMinimapOffsetX = this.minimapOffsetX;
      this.mouseDownMinimapOffsetY = this.minimapOffsetY;

      this.mouseDown = true;
    }
  }

  handleMouseMove = (event) => {
    if (this.minimapCanvas.current == null) { return; }

    this.minimapMouseX = event.pageX - this.minimapCanvas.current.offsetLeft;
    this.minimapMouseY = event.pageY - this.minimapCanvas.current.offsetTop;

    if (this.mouseDown) {
      event.stopPropagation();
      event.preventDefault();

      this.minimapOffsetX = this.mouseDownMinimapOffsetX + this.minimapMouseX - this.mouseDownMinimapMouseX;
      this.minimapOffsetY = this.mouseDownMinimapOffsetY + this.minimapMouseY - this.mouseDownMinimapMouseY;

      this.restrictMinimapOffset();

      if (this.mounted) { this.drawCanvas(false); }
    }

    this.updateMinimapImageLoadingPriorities();

    if (this.minimapMouseHover) {
      this.clientMouseX = event.clientX;
      this.clientMouseY = event.clientY;

      this.mouseMoveCoordinateX = (this.minimapMouseX - this.minimapOffsetX) / (this.minimapScale * this.minimapCanvas.current.width);
      this.mouseMoveCoordinateY = (this.minimapMouseY - this.minimapOffsetY) / (this.minimapScale * this.minimapCanvas.current.height);

      this.setTooltipLines();

      this.setState({});
    }
  }

  handleMouseUp = (event) => {
    if (this.mouseDown) {
      event.stopPropagation();
      event.preventDefault();

      this.mouseDown = false;

      this.setSearchParameters();
    }
  }

  handleResize = (event) => {
    this.updateMinimapDimensions();
  }

  handleSetMinimapViewportClick = (battleground, minimapScale, minimapOffsetX, minimapOffsetY, event) => {
    this.battleground.current.value = battleground;

    this.selectBattleground();

    this.setMinimapViewport(minimapScale, minimapOffsetX, minimapOffsetY);

    this.drawCanvas(true);

    this.setSearchParameters();
  }

  minimapScaleDecrement(minimapScale) {
    let scaleStep = 0.1;
    let scaleRange = 2.0;

    while (minimapScale > scaleRange) {
      scaleStep *= 2.0;
      scaleRange *= 2.0;
    }

    return scaleStep;
  }

  minimapScaleIncrement(minimapScale) {
    let scaleStep = 0.1;
    let scaleRange = 2.0;

    while (minimapScale >= scaleRange) {
      scaleStep *= 2.0;
      scaleRange *= 2.0;
    }

    return scaleStep;
  }

  mouseEnterMinimap = (event) => {
    this.minimapMouseHover = true;
  }

  mouseLeaveMinimap = (event) => {
    this.minimapMouseHover = false;

    this.setState({});
  }

  mouseMoveMinimap = (event) => {
    this.mouseMinimapX = event.nativeEvent.offsetX;
    this.mouseMinimapY = event.nativeEvent.offsetY;
  }

  parseTransitData(battlegroundName, transitData) {
    const transitDataRailNames = Object.keys(transitData["rails"]);

    const transitDataVehicles = transitData["vehicles"];

    const transitDataVehicleTypes = transitData["vehicleTypes"];

    const transitDataCarTypes = transitData["carTypes"];

    for (let i = 0; i < transitDataRailNames.length; i++) {
      const transitDataRailNodes = transitData["rails"][transitDataRailNames[i]]["railNodes"];

      let railNodes = [];

      for (let j = 0; j < transitDataRailNodes.length; j++) {
        const transitDataSpline = transitDataRailNodes[j]["spline"];

        const transitDataSplinePoints = transitDataSpline["points"];

        let startCoordinate = [];

        let railCurves = [];

        let arrivePosition = [],
            arriveTangent = [],
            leavePosition = [],
            leaveTangent = [];

        let interpolationMode;

        for (let k = 0; k < transitDataSplinePoints.length; k++) {
          switch (k) {
          case 0:
            leavePosition = [transitDataSplinePoints[k]["outVal"][0] / this.battlegrounds[battlegroundName].width,
                             transitDataSplinePoints[k]["outVal"][1] / this.battlegrounds[battlegroundName].height];

            interpolationMode = transitDataSplinePoints[k]["interpMode"];

            if ((interpolationMode === "CIM_CurveUser") || (interpolationMode === "CIM_CurveAuto")) {
              leaveTangent = [transitDataSplinePoints[k]["leaveTangent"][0] / this.battlegrounds[battlegroundName].width,
                              transitDataSplinePoints[k]["leaveTangent"][1] / this.battlegrounds[battlegroundName].height];
            }

            startCoordinate = leavePosition;

            break;
          case transitDataSplinePoints.length - 1:
            arrivePosition = [transitDataSplinePoints[k]["outVal"][0] / this.battlegrounds[battlegroundName].width,
                              transitDataSplinePoints[k]["outVal"][1] / this.battlegrounds[battlegroundName].height];

            if ((interpolationMode === "CIM_CurveUser") || (interpolationMode === "CIM_CurveAuto")) {
              arriveTangent = [transitDataSplinePoints[k]["arriveTangent"][0] / this.battlegrounds[battlegroundName].width,
                               transitDataSplinePoints[k]["arriveTangent"][1] / this.battlegrounds[battlegroundName].height];

              railCurves.push(new BezierCurve(leavePosition, leaveTangent, arriveTangent, arrivePosition));
            } else if (interpolationMode === "CIM_Linear") {
              railCurves.push(new LinearCurve(leavePosition, arrivePosition));
            }

            break;
          default:
            arrivePosition = [transitDataSplinePoints[k]["outVal"][0] / this.battlegrounds[battlegroundName].width,
                              transitDataSplinePoints[k]["outVal"][1] / this.battlegrounds[battlegroundName].height];

            if ((interpolationMode === "CIM_CurveUser") || (interpolationMode === "CIM_CurveAuto")) {
              arriveTangent = [transitDataSplinePoints[k]["arriveTangent"][0] / this.battlegrounds[battlegroundName].width,
                               transitDataSplinePoints[k]["arriveTangent"][1] / this.battlegrounds[battlegroundName].height];

              railCurves.push(new BezierCurve(leavePosition, leaveTangent, arriveTangent, arrivePosition));
            } else if (interpolationMode === "CIM_Linear") {
              railCurves.push(new LinearCurve(leavePosition, arrivePosition));
            }

            interpolationMode = transitDataSplinePoints[k]["interpMode"];

            leavePosition = arrivePosition;

            if ((interpolationMode === "CIM_CurveUser") || (interpolationMode === "CIM_CurveAuto")) {
              leaveTangent = [transitDataSplinePoints[k]["leaveTangent"][0] / this.battlegrounds[battlegroundName].width,
                              transitDataSplinePoints[k]["leaveTangent"][1] / this.battlegrounds[battlegroundName].height];
            }

            break;
          }
        }

        const transitDataSplineReparamTable = transitDataSpline["reparamTable"];

        let interpolationRanges = [];

        let inVal,
            outVal;

        for (let k = 0; k < transitDataSplineReparamTable.length; k++) {
          interpolationRanges.push(new LinearRange(transitDataSplineReparamTable[k]["inVal"], transitDataSplineReparamTable[k]["outVal"]));
        }

        let reparameterizationTable = new InterpolationTable(interpolationRanges);

        const transitDataMoveCurve = transitDataRailNodes[j]["moveCurve"];

        const transitDataMoveCurveKeys = transitDataMoveCurve["keys"];

        interpolationRanges = [];

        let previousInVal;

        for (let k = 0; k < transitDataMoveCurveKeys.length; k++) {
          switch (k) {
          case 0:
            inVal = transitDataMoveCurveKeys[k]["time"];
            outVal = transitDataMoveCurveKeys[k]["value"];

            interpolationRanges.push(new LinearRange(inVal, outVal));

            interpolationMode = transitDataMoveCurveKeys[k]["interpMode"];

            if (interpolationMode === "RCIM_Cubic") {
              previousInVal = inVal;

              leaveTangent = transitDataMoveCurveKeys[k]["leaveTangent"];
            }

            break;
          case transitDataMoveCurveKeys.length - 1:
            inVal = transitDataMoveCurveKeys[k]["time"];
            outVal = transitDataMoveCurveKeys[k]["value"];

            if (interpolationMode === "RCIM_Cubic") {
              arriveTangent = transitDataMoveCurveKeys[k]["arriveTangent"];

              const inValDifference = inVal - previousInVal;

              interpolationRanges.push(new CubicSplineRange(leaveTangent * inValDifference, arriveTangent * inValDifference, inVal, outVal));
            } else if (interpolationMode === "RCIM_Linear") {
              interpolationRanges.push(new LinearRange(inVal, outVal));
            }

            break;
          default:
            inVal = transitDataMoveCurveKeys[k]["time"];
            outVal = transitDataMoveCurveKeys[k]["value"];

            if (interpolationMode === "RCIM_Cubic") {
              arriveTangent = transitDataMoveCurveKeys[k]["arriveTangent"];

              const inValDifference = inVal - previousInVal;

              interpolationRanges.push(new CubicSplineRange(leaveTangent * inValDifference, arriveTangent * inValDifference, inVal, outVal));
            } else if (interpolationMode === "RCIM_Linear") {
              interpolationRanges.push(new LinearRange(inVal, outVal));
            }

            interpolationMode = transitDataMoveCurveKeys[k]["interpMode"];

            if (interpolationMode === "RCIM_Cubic") {
              previousInVal = inVal;

              leaveTangent = transitDataMoveCurveKeys[k]["leaveTangent"];
            }

            break;
          }
        }

        let moveCurve = new InterpolationTable(interpolationRanges);

        railNodes.push(new RailNode(startCoordinate, railCurves, reparameterizationTable, moveCurve, transitDataRailNodes[j]["inverseMove"]));
      }

      let railVehicles = [];

      for (let j = 0; j < transitDataVehicles.length; j++) {
        if (transitDataVehicles[j]["railName"] === transitDataRailNames[i]) {
          const transitDataVehicleType = transitDataVehicleTypes[transitDataVehicles[j]["vehicleType"]];

          const transitDataVehicleRailCars = transitDataVehicleType["railCars"];

          let railCars = [];

          for (let k = 0; k < transitDataVehicleRailCars.length; k++) {
            let transitDataCarType = transitDataCarTypes[transitDataVehicleRailCars[k]["carType"]];

            if (transitDataCarType.duplicateOf != null) {
              transitDataCarType = transitDataCarTypes[transitDataCarType.duplicateOf];
            }

            let railCarPath = new Path2D();

            railCarPath.moveTo(transitDataCarType["simpleOutline"][0][0] / this.battlegrounds[battlegroundName].width,
                               transitDataCarType["simpleOutline"][0][1] / this.battlegrounds[battlegroundName].height);

            for (let l = 1; l < transitDataCarType["simpleOutline"].length; l++) {
              railCarPath.lineTo(transitDataCarType["simpleOutline"][l][0] / this.battlegrounds[battlegroundName].width,
                                 transitDataCarType["simpleOutline"][l][1] / this.battlegrounds[battlegroundName].height);
            }

            railCarPath.closePath();

            railCars.push(new RailCar(transitDataVehicleRailCars[k]["location"], transitDataVehicleRailCars[k]["frontContact"]["relativeLocation"], transitDataVehicleRailCars[k]["backContact"]["relativeLocation"], railCarPath));
          }

          const locationOffset = railCars[0].location[0] + railCars[0].frontContact[0];

          for (let k = 0; k < railCars.length; k++) {
            railCars[k].location[0] -= locationOffset;
          }

          railVehicles.push(new RailVehicle(transitDataVehicles[j]["startRailIndex"], transitDataVehicleType["loop"], railCars));
        }
      }

      const railStations = transitData["rails"][transitDataRailNames[i]]["stations"];

      for (let j = 0; j < railStations.length; j++) {
        let railStationIndex = -1;

        for (let k = 0; k < this.railStations.length; k++) {
          if (this.railStations[k].battleground !== battlegroundName) { continue; }

          if (this.railStations[k].name === railStations[j]) {
            railStationIndex = k;

            break;
          }
        }

        if (railStationIndex === -1) {
          railStationIndex = this.railStations.push(new RailStation(battlegroundName, railStations[j]));
        }
      }

      this.rails.push(new Rail(battlegroundName, transitData["rails"][transitDataRailNames[i]]["colorRGB"], transitData["rails"][transitDataRailNames[i]]["lineDash"], railNodes, railVehicles, railStations));
    }

    this.constructRailStationsSchedule(battlegroundName);

    if (this.battleground.current != null) {
      if (this.battleground.current.value === battlegroundName) {
        this.setBattlegroundRails();

        this.setBattlegroundRailStations();

        if (this.mounted) {
          this.drawCanvas(true);
        }
      }
    }
  }

  pause = (event) => {
    this.playing = false;

    this.setState({});

    if (this.playIntervalID != null) {
      clearInterval(this.playIntervalID);
      this.playIntervalID = null;
    }
  }

  play = (event) => {
    this.playing = true;

    this.setState({});

    if (this.playIntervalID == null) {
      this.playIntervalID = setInterval(this.playInterval.bind(this), this.playIntervalMilliseconds);
    }
  }

  playInterval() {
    this.timeSeconds += this.playSpeed * 0.001 * this.playIntervalMilliseconds;

    this.timeSeconds = Math.min(this.timeSeconds, this.totalRoundTimeSeconds);

    if (this.time.current != null) {
      this.time.current.value = this.timeSeconds;
    }

    this.setTooltipLines();

    this.drawCanvas(true);
  }

  restrictMinimapOffset() {
    if (isNaN(this.minimapOffsetY)) {
      this.minimapOffsetY = 0;
    } else if (this.minimapOffsetY > 0) {
      this.minimapOffsetY = 0;
    } else if (this.minimapCanvas.current != null) {
      if (this.minimapOffsetY < this.minimapCanvas.current.height * (1.0 - this.minimapScale)) {
        this.minimapOffsetY = this.minimapCanvas.current.height * (1.0 - this.minimapScale);
      }
    }

    if (isNaN(this.minimapOffsetX)) {
      this.minimapOffsetX = 0;
    } else if (this.minimapOffsetX > 0) {
      this.minimapOffsetX = 0;
    } else if (this.minimapCanvas.current != null) {
      if (this.minimapOffsetX < this.minimapCanvas.current.width * (1.0 - this.minimapScale)) {
        this.minimapOffsetX = this.minimapCanvas.current.width * (1.0 - this.minimapScale);
      }
    }
  }

  restrictMinimapScale() {
    if (isNaN(this.minimapScale)) {
      this.minimapScale = 0;
    } else if (this.minimapScale < 1.0) {
      this.minimapScale = 1.0;
    } else if (this.minimapScale > this.maximumMinimapScale) {
      this.minimapScale = this.maximumMinimapScale;
    }
  }

  resumePlayInterval = (event) => {
    if (this.playing && (this.playIntervalID == null)) {
      this.playIntervalID = setInterval(this.playInterval.bind(this), this.playIntervalMilliseconds);
    }
  }

  scaleMinimap = (event) => {
    event.stopPropagation();
    event.preventDefault();

    if (this.minimapCanvas.current == null) { return; }

    const previousMinimapScale = this.minimapScale;

    if (event.deltaY < 0) {
      this.minimapScale += this.minimapScaleIncrement(this.minimapScale);
    } else {
      this.minimapScale -= this.minimapScaleDecrement(this.minimapScale);
    }

    this.restrictMinimapScale();

    if (this.minimapScale === previousMinimapScale) { return; }

    const previousMinimapOffsetX = this.minimapOffsetX;
    const previousMinimapOffsetY = this.minimapOffsetY;

    this.minimapOffsetX += (this.mouseMinimapX - this.minimapOffsetX) * (previousMinimapScale - this.minimapScale) / previousMinimapScale;
    this.minimapOffsetY += (this.mouseMinimapY - this.minimapOffsetY) * (previousMinimapScale - this.minimapScale) / previousMinimapScale;

    this.restrictMinimapOffset();

    if (this.mounted) {
      this.drawCanvas(false);
    }

    if ((previousMinimapOffsetX !== this.minimapOffsetX) || (previousMinimapOffsetY !== this.minimapOffsetY)) {
      if (this.mouseDown) {
        this.mouseDownMinimapMouseX = this.minimapMouseX;
        this.mouseDownMinimapMouseY = this.minimapMouseY;

        this.mouseDownMinimapOffsetX = this.minimapOffsetX;
        this.mouseDownMinimapOffsetY = this.minimapOffsetY;
      }

      this.mouseMoveCoordinateX = (this.minimapMouseX - this.minimapOffsetX) / (this.minimapScale * this.minimapCanvas.current.width);
      this.mouseMoveCoordinateY = (this.minimapMouseY - this.minimapOffsetY) / (this.minimapScale * this.minimapCanvas.current.height);

      this.setTooltipLines();

      this.setState({});
    }

    this.updateMinimapImageLoadingPriorities();

    window.clearTimeout (this.wheelTimeoutID);

    this.wheelTimeoutID = window.setTimeout(this.setSearchParametersTimeout, 100, this);
  }

  selectBattleground = (event) => {
    if (this.battleground.current == null) { return; }

    let battlegrounds = Object.keys(this.battlegrounds);
    for (let i = 0; i < battlegrounds.length; i++) { this.battlegrounds[battlegrounds[i]].minimap.loadingImages = false; }

    this.battlegrounds[this.battleground.current.value].minimap.loadImages();

    this.updateMinimapImageLoadingPriorities();

    this.setBattlegroundRails();

    this.setBattlegroundRailStations();

    this.setPhaseValuesForAllPhases();

    this.updateTotalRoundTime();

    this.maximumMinimapScale = this.battlegroundMaximumMinimapScale(this.battleground.current.value);

    if (this.mounted) {
      this.drawCanvas(false);

      this.setState({});
    }

    this.setSearchParameters();
  }

  setBattlegroundRails() {
    if (this.battleground.current == null) { return; }

    this.battlegroundRails = [];

    for (let i = 0; i < this.rails.length; i++) {
      if (this.rails[i].battleground === this.battleground.current.value) {
        this.battlegroundRails.push(this.rails[i]);
      }
    }
  }

  setBattlegroundRailStations() {
    if (this.battleground.current == null) { return; }

    this.battlegroundRailStations = [];

    for (let i = 0; i < this.railStations.length; i++) {
      if (this.railStations[i].battleground === this.battleground.current.value) {
        this.battlegroundRailStations.push(this.railStations[i]);
      }
    }

    this.setTimeTableTableHeaderWidth();
  }

  setRailInformationTableHeaderWidth() {
    const railsInformationHeaderTable = <table>
                                          <tbody>
                                            <tr>
                                              <td className="header">&nbsp;</td>
                                            </tr>
                                            <tr className="header alternateBackground">
                                              <td className="alternateBackground">Total Travel Time<span className="spacer" style={{ fontSize : "1.4em" }}>*</span></td>
                                            </tr>
                                            <tr>
                                              <td className="header">Number of Stops</td>
                                            </tr>
                                          </tbody>
                                        </table>;

    railsInformationHeaderMeasurementRoot.render(railsInformationHeaderTable);
  }

  setTimeTableTableHeaderWidth() {
    let timeTableTableRows = [];

    timeTableTableRows.push(<tr><td className="header">&nbsp;</td></tr>);

    for (let i = 0; i < this.battlegroundRailStations.length; i++) {
      if ((i % 2) === 0) {
        timeTableTableRows.push(<tr><td className="header alternateBackground">{this.battlegroundRailStations[i].name}</td></tr>);
      } else {
        timeTableTableRows.push(<tr><td className="header">{this.battlegroundRailStations[i].name}</td></tr>);
      }
    }

    const timeTableTableHeaderTable = <table>
                                        <tbody>
                                          {timeTableTableRows}
                                        </tbody>
                                      </table>;

    timeTableTableHeaderMeasurementRoot.render(timeTableTableHeaderTable);
  }

  setMinimapViewport(minimapScale, minimapOffsetX, minimapOffsetY) {
    let modifiedMinimapViewport = false;

    if (this.minimapScale !== minimapScale) {
      this.minimapScale = minimapScale;

      this.restrictMinimapScale();

      modifiedMinimapViewport = true;
    }

    if ((this.minimapOffsetX !== minimapOffsetX) || (this.minimapOffsetY !== minimapOffsetY)) {
      this.minimapOffsetX = minimapOffsetX;
      this.minimapOffsetY = minimapOffsetY;

      this.restrictMinimapOffset();

      modifiedMinimapViewport = true;
    }

    if (modifiedMinimapViewport) { this.updateMinimapImageLoadingPriorities(); }
  }

  setPhaseValuesForAllPhases = (event) => {
    if (this.battleground.current == null) { return; }
    if (this.battlegrounds[this.battleground.current.value] == null) { return; }

    for (let i = 0; i < 9; i++) {
      if (this.phases[i].current == null) { return; }

      if ((this.phases[i].current.delay.current == null) ||
          (this.phases[i].current.wait.current == null) ||
          (this.phases[i].current.move.current == null) ||
          (this.phases[i].current.dps.current == null) ||
          (this.phases[i].current.shrink.current == null) ||
          (this.phases[i].current.spread.current == null) ||
          (this.phases[i].current.landRatio.current == null)) {
        return;
      }
    }

    const battleground = this.battlegrounds[this.battleground.current.value];

    let phasesValues = "";
    if (battleground.phasesDefaultValues != null) { phasesValues = "Default"; }
    if (this.phasesValues.current != null) {
      if (this.phasesValues.current.value !== "") { phasesValues = this.phasesValues.current.value; }
    }

    if (phasesValues === "Default") {
      for (let i = 0; i < 9; i++) {
        this.phases[i].current.delay.current.value = battleground.phasesDefaultValues[i].delay;
        this.phases[i].current.wait.current.value = battleground.phasesDefaultValues[i].wait;
        this.phases[i].current.move.current.value = battleground.phasesDefaultValues[i].move;
        this.phases[i].current.dps.current.value = battleground.phasesDefaultValues[i].dps;
        this.phases[i].current.shrink.current.value = battleground.phasesDefaultValues[i].shrink;
        this.phases[i].current.spread.current.value = battleground.phasesDefaultValues[i].spread;
        this.phases[i].current.landRatio.current.value = battleground.phasesDefaultValues[i].landRatio;
      }
    } else {
      for (let i = 0; i < battleground.specialNormalModes.length; i++) {
        if (this.phasesValues.current.value === battleground.specialNormalModes[i].name) {
          for (let j = 0; j < 9; j++) {
            this.phases[j].current.delay.current.value = battleground.specialNormalModes[i].phasesDefaultValues[j].delay;
            this.phases[j].current.wait.current.value = battleground.specialNormalModes[i].phasesDefaultValues[j].wait;
            this.phases[j].current.move.current.value = battleground.specialNormalModes[i].phasesDefaultValues[j].move;
            this.phases[j].current.dps.current.value = battleground.specialNormalModes[i].phasesDefaultValues[j].dps;
            this.phases[j].current.shrink.current.value = battleground.specialNormalModes[i].phasesDefaultValues[j].shrink;
            this.phases[j].current.spread.current.value = battleground.specialNormalModes[i].phasesDefaultValues[j].spread;
            this.phases[j].current.landRatio.current.value = battleground.specialNormalModes[i].phasesDefaultValues[j].landRatio;
          }
        }
      }
    }

    if (this.mounted) {
      this.drawCanvas(false);
    } else {
      this.setState({});
    }
  }

  setSearchParameters = (event) => {
    if (this.mounted) {
      let searchParameters = "?";

      if (this.battleground.current != null) {
        if (searchParameters !== "?") { searchParameters += "&"; }

        searchParameters += "m=" + this.battleground.current.value;
      }

      if (searchParameters !== "?") { searchParameters += "&"; }

      searchParameters += "ms=" + this.minimapScale.toFixed(1);

      if (this.minimapCanvas.current != null) {
        if (searchParameters !== "?") { searchParameters += "&"; }

        searchParameters += "mx=" + ((-this.minimapOffsetX + 0.5 * this.minimapCanvas.current.width) / (this.minimapScale * this.minimapCanvas.current.width)).toFixed(4);

        if (searchParameters !== "?") { searchParameters += "&"; }

        searchParameters += "my=" + ((-this.minimapOffsetY + 0.5 * this.minimapCanvas.current.height) / (this.minimapScale * this.minimapCanvas.current.height)).toFixed(4);
      }

      window.history.replaceState(null, "", searchParameters);
    }
  }

  setSearchParametersTimeout(movingTrainSchedule) {
    movingTrainSchedule.setSearchParameters();
  }

  setTime(seconds) {
    this.timeSeconds = Math.min(Math.max(seconds, 0), this.totalRoundTimeSeconds);

    if (this.time.current != null) {
      this.time.current.value = this.timeSeconds;
    }

    this.setTooltipLines();

    this.drawCanvas(true);
  }

  setTooltipLines() {
    this.tooltipLines = [];

    if ((this.minimapCanvas.current != null) && (this.battleground.current != null)) {
      const context = this.minimapCanvas.current.getContext('2d', { alpha: false });

      for (let i = 0; i < this.battlegroundRails.length; i++) {
        for (let j = 0; j < this.battlegroundRails[i].railVehicles.length; j++) {
          if (context.isPointInPath(this.battlegroundRails[i].railVehicles[j].clipPath, this.minimapMouseX, this.minimapMouseY)) {
            let tooltipLineText;

            const roundedTimeToDeparture = Math.round(this.battlegroundRails[i].railVehicles[j].timeToDeparture);

            if (roundedTimeToDeparture <= 0) {
              const roundedTimeToNextRailStation = Math.round(this.battlegroundRails[i].railVehicles[j].timeToNextRailStation);

              if (roundedTimeToNextRailStation > 0) {
                tooltipLineText = "Arriving at " + this.battlegroundRails[i].railVehicles[j].nextRailStation + " in " + this.convertSecondsToMinutesAndSecondsString(roundedTimeToNextRailStation);
              } else {
                tooltipLineText = "Departing for " + this.battlegroundRails[i].railVehicles[j].nextNextRailStation + " in " + this.convertSecondsToMinutesAndSecondsString(Math.round(this.battlegroundRails[i].railVehicles[j].timeToNextDeparture));
              }
            } else {
              tooltipLineText = "Departing for " + this.battlegroundRails[i].railVehicles[j].nextRailStation + " in " + this.convertSecondsToMinutesAndSecondsString(roundedTimeToDeparture);
            }

            this.tooltipLines.push(<TooltipLine lineColorRGB={this.battlegroundRails[i].colorRGB} lineDash={this.battlegroundRails[i].lineDash} text={tooltipLineText} />);
          }
        }
      }
    }

    const tooltipDiv = <div className="tooltip" style={{ display : 'table', visibility : 'hidden', position : 'relative', left : 0, top : 0 }}>
                         <table>
                           <tbody>
                             {this.tooltipLines}
                           </tbody>
                         </table>
                       </div>;

    tooltipMeasurementRoot.render(tooltipDiv);
  }

  touchEnd = (event) => {
    switch (event.touches.length) {
    case 0:
      this.setSearchParameters();

      break;
    case 1:
      event.stopPropagation();
      event.preventDefault();

      this.setSearchParameters();

      this.touchInit(event);

      break;
    default:
      break;
    }

    this.touchRecentlyEnded = true;

    window.clearTimeout (this.touchRecentlyEndedTimeoutID);

    this.touchRecentlyEndedTimeoutID = window.setTimeout(this.clearTouchRecentlyEndedTimeout, 800, this);
  }

  touchInit(event) {
    this.touchStartMinimapTouchX = event.touches[0].pageX - this.minimapCanvas.current.offsetLeft;
    this.touchStartMinimapTouchY = event.touches[0].pageY - this.minimapCanvas.current.offsetTop;

    this.touchStartMinimapOffsetX = this.minimapOffsetX;
    this.touchStartMinimapOffsetY = this.minimapOffsetY;
  }

  touchMove = (event) => {
    if (this.minimapCanvas.current == null) { return; }

    switch (event.touches.length) {
    case 1:
      event.stopPropagation();
      event.preventDefault();

      const minimapTouchX = event.touches[0].pageX - this.minimapCanvas.current.offsetLeft;
      const minimapTouchY = event.touches[0].pageY - this.minimapCanvas.current.offsetTop;

      this.minimapOffsetX = this.touchStartMinimapOffsetX + minimapTouchX - this.touchStartMinimapTouchX;
      this.minimapOffsetY = this.touchStartMinimapOffsetY + minimapTouchY - this.touchStartMinimapTouchY;

      this.restrictMinimapOffset();

      if (this.mounted) { this.drawCanvas(false); }

      break;
    case 2:
      event.stopPropagation();
      event.preventDefault();

      const pinchMinimapCenterX = 0.5 * (event.touches[0].pageX + event.touches[1].pageX) - this.minimapCanvas.current.offsetLeft;
      const pinchMinimapCenterY = 0.5 * (event.touches[0].pageY + event.touches[1].pageY) - this.minimapCanvas.current.offsetTop;

      const pinchMinimapDistance = Math.sqrt((event.touches[0].pageX - event.touches[1].pageX) ** 2 +
                                             (event.touches[0].pageY - event.touches[1].pageY) ** 2);

      const previousMinimapScale = this.minimapScale;

      this.minimapScale = pinchMinimapDistance / this.pinchStartMinimapDistance * this.pinchStartMinimapScale;

      this.restrictMinimapScale();

      this.minimapOffsetX += (pinchMinimapCenterX - this.minimapOffsetX) * (previousMinimapScale - this.minimapScale) / previousMinimapScale;
      this.minimapOffsetY += (pinchMinimapCenterY - this.minimapOffsetY) * (previousMinimapScale - this.minimapScale) / previousMinimapScale;

      this.minimapOffsetX += pinchMinimapCenterX - this.pinchPreviousMinimapCenterX;
      this.minimapOffsetY += pinchMinimapCenterY - this.pinchPreviousMinimapCenterY;

      this.restrictMinimapOffset();

      if (this.mounted) { this.drawCanvas(false); }

      this.pinchPreviousMinimapCenterX = pinchMinimapCenterX;
      this.pinchPreviousMinimapCenterY = pinchMinimapCenterY;

      break;
    default:
      break;
    }

    this.touchRecentlyMoved = true;

    window.clearTimeout (this.touchRecentlyMovedTimeoutID);

    this.touchRecentlyMovedTimeoutID = window.setTimeout(this.clearTouchRecentlyMovedTimeout, 800, this);
  }

  touchStart = (event) => {
    if (this.minimapCanvas.current == null) { return; }

    switch (event.touches.length) {
    case 1:
      event.stopPropagation();
      event.preventDefault();

      this.touchInit(event);

      break;
    case 2:
      event.stopPropagation();
      event.preventDefault();

      this.pinchPreviousMinimapCenterX = 0.5 * (event.touches[0].pageX + event.touches[1].pageX) - this.minimapCanvas.current.offsetLeft;
      this.pinchPreviousMinimapCenterY = 0.5 * (event.touches[0].pageY + event.touches[1].pageY) - this.minimapCanvas.current.offsetTop;

      this.pinchStartMinimapDistance = Math.sqrt((event.touches[0].pageX - event.touches[1].pageX) ** 2 +
                                                 (event.touches[0].pageY - event.touches[1].pageY) ** 2);

      this.pinchStartMinimapScale = this.minimapScale;

      break;
    default:
      break;
    }
  }

  updateHighResolutionMinimapCanvas() {
    if (this.userAgentIsSafari) { return; }

    if (this.highResolutionMinimapCanvas.current == null) { return; }

    if (this.battleground.current == null) { return; }

    const minimap = this.battlegrounds[this.battleground.current.value].minimap;

    this.highResolutionMinimapCanvas.current.width = minimap.highResolutionDimensions[0];
    this.highResolutionMinimapCanvas.current.height = minimap.highResolutionDimensions[1];

    const highResolutionContext = this.highResolutionMinimapCanvas.current.getContext('2d', { alpha: true });

    highResolutionContext.clearRect(0, 0, minimap.highResolutionDimensions[0], minimap.highResolutionDimensions[1]);

    const highResolutionImageWidth = Math.floor(minimap.divisionFractions[0] * minimap.highResolutionDimensions[0]);
    const highResolutionImageHeight = Math.floor(minimap.divisionFractions[1] * minimap.highResolutionDimensions[1]);

    for (let i = 0; i < minimap.highResolutionImages.length; i++) {
      if (minimap.highResolutionImages[i].loaded) {
        const highResolutionImageOffsetX = minimap.highResolutionImages[i].indexes[0] * highResolutionImageWidth;
        const highResolutionImageOffsetY = minimap.highResolutionImages[i].indexes[1] * highResolutionImageHeight;

        highResolutionContext.drawImage(minimap.highResolutionImages[i].image, highResolutionImageOffsetX, highResolutionImageOffsetY);
      }
    }
  }

  updateMinimapDimensions = (event) => {
    if (this.minimapCanvas.current == null) { return false; }

    const minimumSize = 508;

    let minimapSize = 0;

    if (document.documentElement.clientHeight < (document.documentElement.clientWidth - minimumSize / 2)) {
      minimapSize = document.documentElement.clientHeight;

      if (this.minimapSpacer.current != null) {
        minimapSize -= 6.6 * this.minimapSpacer.current.clientHeight;
      }

      if (this.settings.current != null) {
        let maxWidth = document.documentElement.clientWidth - this.settings.current.offsetLeft - this.settings.current.clientWidth;

        if (this.minimapSpacer.current != null) {
          maxWidth -= 2.5 * this.minimapSpacer.current.clientHeight;
        }

        if (minimapSize > maxWidth) { minimapSize = maxWidth; }
      }
    }

    if (minimapSize < minimumSize) {
      minimapSize = document.documentElement.clientWidth;

      if (this.minimapSpacer.current != null) {
        minimapSize -= 2.0 * this.minimapSpacer.current.clientHeight;
      }
    }

    if (minimapSize < minimumSize) { minimapSize = minimumSize; }

    minimapSize = Math.trunc(minimapSize);

    if ((this.minimapCanvas.current.width === minimapSize) && (this.minimapCanvas.current.height === minimapSize)) {
      return false;
    }

    const mx = (-this.minimapOffsetX + 0.5 * this.minimapCanvas.current.width) / (this.minimapScale * this.minimapCanvas.current.width);
    this.minimapOffsetX = -mx * this.minimapScale * minimapSize + 0.5 * minimapSize;
    const my = (-this.minimapOffsetY + 0.5 * this.minimapCanvas.current.height) / (this.minimapScale * this.minimapCanvas.current.height);
    this.minimapOffsetY = -my * this.minimapScale * minimapSize + 0.5 * minimapSize;

    this.minimapCanvas.current.width = minimapSize;
    this.minimapCanvas.current.height = minimapSize;

    this.updateMinimapImageLoadingPriorities();

    this.drawCanvas(true);

    return true;
  }

  updateMinimapImageLoadingPriorities() {
    if (!this.battlegrounds[this.battleground.current.value].minimap.loadingImages) { return; }

    if (this.battleground.current == null) { return; }
    if (this.minimapCanvas.current == null) { return; }

    let minimap = this.battlegrounds[this.battleground.current.value].minimap;

    const highResolutionImageWidth = this.minimapScale * this.minimapCanvas.current.width * minimap.divisionFractions[0];
    const highResolutionImageHeight = this.minimapScale * this.minimapCanvas.current.height * minimap.divisionFractions[1];

    let maxPriority = 0;

    for (let i = 0; i < minimap.highResolutionImages.length; i++) {
      if (!minimap.highResolutionImages[i].loading && !minimap.highResolutionImages[i].loaded) {
        let priority = 0;

        const highResolutionImageOffsetX = this.minimapOffsetX + minimap.highResolutionImages[i].indexes[0] * highResolutionImageWidth;
        const highResolutionImageOffsetY = this.minimapOffsetY + minimap.highResolutionImages[i].indexes[1] * highResolutionImageHeight;

        if ((highResolutionImageOffsetX + highResolutionImageWidth > 0) && (highResolutionImageOffsetX <= this.minimapCanvas.current.width) &&
            (highResolutionImageOffsetY + highResolutionImageHeight > 0) && (highResolutionImageOffsetY <= this.minimapCanvas.current.height)) {
          priority++;
        }

        if (this.minimapMouseHover &&
            (highResolutionImageOffsetX < this.mouseMinimapX) && ((highResolutionImageOffsetX + highResolutionImageWidth) > this.mouseMinimapX) &&
            (highResolutionImageOffsetY < this.mouseMinimapY) && ((highResolutionImageOffsetY + highResolutionImageHeight) > this.mouseMinimapY)) {
          priority++;
        }

        this.battlegrounds[this.battleground.current.value].minimap.highResolutionImages[i].priority = priority;

        maxPriority = Math.max(priority, maxPriority);
      }
    }

    minimap.maxPriority = maxPriority
  }

  updateMovingTransitSchedule = (event) => {
    this.drawCanvas(false);
  }

  updatePlaySpeed = (event) => {
    if (this.playSpeedSelect == null) { return; }

    this.playSpeed = Number(this.playSpeedSelect.current.value);
  }

  updateTotalRoundTime = (event) => {
    for (let i = 0; i < 9; i++) {
      if (this.phases[i].current == null) { return; }
      if (this.phases[i].current.delay.current == null) { return; }
      if (this.phases[i].current.move.current == null) { return; }
      if (this.phases[i].current.wait.current == null) { return; }
    }

    let totalRoundTimeSeconds = 0;

    for (let i = 0; i < 9; i++) {
      totalRoundTimeSeconds += Number(this.phases[i].current.delay.current.value) +
                               Number(this.phases[i].current.wait.current.value) +
                               Number(this.phases[i].current.move.current.value);
    }

    this.totalRoundTimeSeconds = totalRoundTimeSeconds;

    if (this.totalRoundTimeSeconds > 0) {
      this.totalRoundTimeSecondsInverse = 1.0 / totalRoundTimeSeconds;
    } else {
      this.totalRoundTimeSecondsInverse = 0.0;
    }

    this.phasesTicks = [];

    let phaseBeginSeconds = 0;

    for (let i = 0; i < 9; i++) {
      const phaseName = "Phase " + (i + 1);

      this.phasesTicks.push(<option key={i} value={phaseBeginSeconds} label={phaseName} />);

      phaseBeginSeconds += Number(this.phases[i].current.delay.current.value) +
                           Number(this.phases[i].current.wait.current.value) +
                           Number(this.phases[i].current.move.current.value);
    }

    this.phasesTicks.push(<option key="10" value={this.totalRoundTimeSeconds} />);

    if (this.timeSeconds > this.totalRoundTimeSeconds) {
      this.timeSeconds = this.totalRoundTimeSeconds;

      if (this.time.current != null) {
        this.time.current.value = this.timeSeconds;
      }
    }
  }

  render() {
    const search = window.location.search;

    const lbzeHREF = "/" + search;
    const loafbzeHREF = "/oaf/" + search;
    const lmtsHREF = "/mts/" + search;

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    let battlegroundName = "";

    if (this.battleground.current != null) { battlegroundName = this.battleground.current.value; }

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    let phasesTableRows = [];
    for (let i = 0; i < 9; i++) {
      phasesTableRows.push(<Phase key={i} number={i+1} delayOnChange={this.updateTotalRoundTime} waitOnChange={this.updateTotalRoundTime} moveOnChange={this.updateTotalRoundTime} dpsOnChange={this.setSearchParameters} shrinkOnChange={this.updateMovingTransitSchedule} spreadOnChange={this.setSearchParameters} landRatioOnChange={this.setSearchParameters} ref={this.phases[i]} />);
    }

    let phasesTable = <table className="phases">
                        <tbody>
                          {phasesTableRows}
                        </tbody>
                      </table>;

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    const timeInputMargin = (this.minimapSpacer.current != null) ? this.minimapSpacer.current.clientWidth : 12;
    const timeInputWidth = (((this.minimapCanvas.current != null) ? this.minimapCanvas.current.width : 254) - 0.8 * timeInputMargin) + "px";
    const timeInput = <input className="time" type="range" min="0" max={this.totalRoundTimeSeconds} defaultValue="0" step="1" list="phasesTicks" onChange={this.haltPlayInterval} onClick={this.resumePlayInterval} ref={this.time} />;

    const timeInputTicks = <datalist id="phasesTicks">
                             {this.phasesTicks}
                           </datalist>;

    const phaseStrings = this.convertSecondsToPhaseStrings(battlegroundName, Math.trunc(this.timeSeconds));

    const timeDiv = <div className="timeInformation">
                      <div className="timeInformationLeft">{phaseStrings[0]}</div>
                      <div className="timeInformationCenter">{this.convertSecondsToMinutesAndSecondsString(Math.trunc(this.timeSeconds))}</div>
                      <div className="timeInformationRight">{phaseStrings[1]}</div>
                    </div>;

    const playPauseSrc = this.playing ? "/images/pause.svg" : "/images/play.svg";
    const playPauseAlt = this.playing ? "Pause" : "Play";
    const playPauseOnClick = this.playing ? this.pause.bind(this) : this.play.bind(this);
    const playPauseInput = <input className="playPause" type="image" src={playPauseSrc} alt={playPauseAlt} onClick={playPauseOnClick} />;

    const playSpeedSelect = <select className="playSpeed" defaultValue="1" onChange={this.updatePlaySpeed} ref={this.playSpeedSelect}>
                              <option value="1">1x</option>
                              <option value="2">2x</option>
                              <option value="8">8x</option>
                              <option value="16">16x</option>
                            </select>;

    const loadingNotificationDivDisplay = this.battleground.current != null ? this.battlegrounds[this.battleground.current.value].minimap.anyHighResolutionImageLoading ? 'inline' : 'none' : 'none';
    const loadingNotificationDiv = <div className="loadingNotification" style={{ display : loadingNotificationDivDisplay }}>Loading High Resolution Map…</div>;

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    const emMeasurement = document.getElementById('emMeasurement').clientHeight;

//--  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --

    const railsInformationHeaderMeasurementDiv = document.getElementById('railsInformationHeaderMeasurement');

    const totalTravelTimeTip = "For looping transit, the time it takes to return to the starting station.  Otherwise the time it takes to visit every station, starting from either end of the line.";

    let railColorLineTableCells = [];
    let railDurationTableCells = [];
    let railNumberOfStationsTableCells = [];

    for (let i = 0; i < this.battlegroundRails.length; i++) {
      if (i > 0) {
        if (this.battlegroundRails[i].colorRGB === this.battlegroundRails[i-1].colorRGB) { continue; }
      }
      railColorLineTableCells.push(<td className="scrollable"><ColorLineCanvas width={5 * emMeasurement} height={emMeasurement} backgroundColor='#2d2d2f' lineColorRGBs={[this.battlegroundRails[i].colorRGB]} lineDashes={[this.battlegroundRails[i].lineDash]} /></td>);
      railDurationTableCells.push(<td className="scrollable railInformation"><span className="spacer" style={{ fontSize : "1.4em" }}>*</span>{this.convertSecondsToMinutesAndSecondsString(Math.round(this.battlegroundRails[i].duration))}<span className="spacer" style={{ fontSize : "1.4em" }}>*</span></td>);
      railNumberOfStationsTableCells.push(<td className="scrollable railInformation">{this.battlegroundRails[i].railStations.length}</td>);
    }

    const railsInformationTable = <table ref={this.railsInformationTable}>
                                    <tbody>
                                      <tr>
                                        <td className="fixedHeader" width={railsInformationHeaderMeasurementDiv.clientWidth}>&nbsp;</td>{railColorLineTableCells}
                                      </tr>
                                      <tr className="alternateBackground">
                                        <td className="fixedHeader alternateBackground" width={railsInformationHeaderMeasurementDiv.clientWidth}>Total Travel Time<span className="info" data-tip={totalTravelTimeTip} data-event='click focus'>*</span></td>{railDurationTableCells}
                                      </tr>
                                      <tr>
                                        <td className="fixedHeader" width={railsInformationHeaderMeasurementDiv.clientWidth}>Number of Stops</td>{railNumberOfStationsTableCells}
                                      </tr>
                                    </tbody>
                                  </table>;

    const railsInformationTableDivMarginLeft = railsInformationHeaderMeasurementDiv.clientWidth + 0.8 * emMeasurement + 2;

    let railsInformationTableDivWidth = 4.4 * emMeasurement;

    if (this.title.current != null) {
      railsInformationTableDivWidth += this.title.current.clientWidth - railsInformationTableDivMarginLeft;

      if (this.credits.current != null) {
        railsInformationTableDivWidth -= this.credits.current.clientWidth;
      }

      railsInformationTableDivWidth -= 1;
    }

//--  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --

    const timeTableTableHeaderMeasurementDiv = document.getElementById('timeTableTableHeaderMeasurement');

    let timeTableTableRows = [];

    let timeTableTableRowCells = [];

    timeTableTableRowCells[0] = [];

    timeTableTableRowCells[0].push(<td className="fixedHeader" width={timeTableTableHeaderMeasurementDiv.clientWidth}>&nbsp;</td>);

    for (let i = 0; i < this.battlegroundRailStations.length; i++) {
      let ii = i + 1;

      timeTableTableRowCells[ii] = [];

      if ((i % 2) === 0) {
        timeTableTableRowCells[ii].push(<td className="fixedHeader alternateBackground" width={timeTableTableHeaderMeasurementDiv.clientWidth}>{this.battlegroundRailStations[i].name}</td>);
      } else {
        timeTableTableRowCells[ii].push(<td className="fixedHeader" width={timeTableTableHeaderMeasurementDiv.clientWidth}>{this.battlegroundRailStations[i].name}</td>);
      }
    }

    let railStationsArrivalIndex = [];

    for (let i = 0; i < this.battlegroundRailStations.length; i++) { railStationsArrivalIndex[i] = 0; }

    let phaseNumber = 1;

    let phaseNumberNumberOfColumns = 0;

    let anyRailStationIncomplete = false;

    for (let i = 0; i < this.battlegroundRailStations.length; i++) {
      anyRailStationIncomplete = anyRailStationIncomplete || (railStationsArrivalIndex[i] < this.battlegroundRailStations[i].arrivals.length);
    }

    let newPhase = true;

    while (anyRailStationIncomplete) {
      let nextArrivalTime = -1;
      let nextArrivalPhaseNumber;

      for (let i = 0; i < this.battlegroundRailStations.length; i++) {
        if (railStationsArrivalIndex[i] >= this.battlegroundRailStations[i].arrivals.length) { continue; }

        if (nextArrivalTime < 0) {
          nextArrivalTime = this.battlegroundRailStations[i].arrivals[railStationsArrivalIndex[i]].time;
          nextArrivalPhaseNumber = this.battlegroundRailStations[i].arrivals[railStationsArrivalIndex[i]].phaseNumber;
        } else if (this.battlegroundRailStations[i].arrivals[railStationsArrivalIndex[i]].time < nextArrivalTime) {
          nextArrivalTime = this.battlegroundRailStations[i].arrivals[railStationsArrivalIndex[i]].time;
          nextArrivalPhaseNumber = this.battlegroundRailStations[i].arrivals[railStationsArrivalIndex[i]].phaseNumber;
        }
      }

      if (nextArrivalPhaseNumber > phaseNumber) {
        newPhase = true;

        if (phaseNumberNumberOfColumns > 0) {
          let phaseTime = 0;

          for (let i = 0; i < (phaseNumber - 1); i++) {
            phaseTime += this.battlegrounds[battlegroundName].phasesDefaultValues[i].delay +
                         this.battlegrounds[battlegroundName].phasesDefaultValues[i].wait +
                         this.battlegrounds[battlegroundName].phasesDefaultValues[i].move;
          }

          timeTableTableRowCells[0].push(<td className="scrollable firstInPhase" colspan={phaseNumberNumberOfColumns}><span className="setTime" onClick={() => this.setTime(phaseTime)}>Phase {phaseNumber}</span></td>);
        }

        phaseNumber = nextArrivalPhaseNumber;
        phaseNumberNumberOfColumns = 0;
      }

      phaseNumberNumberOfColumns++;

      for (let i = 0; i < this.battlegroundRailStations.length; i++) {
        let ii = i + 1;

        let railStationArrival = false;

        if (railStationsArrivalIndex[i] < this.battlegroundRailStations[i].arrivals.length) {
          if ((this.battlegroundRailStations[i].arrivals[railStationsArrivalIndex[i]].time <= (nextArrivalTime + 10)) &&
              (this.battlegroundRailStations[i].arrivals[railStationsArrivalIndex[i]].phaseNumber === nextArrivalPhaseNumber)) {
            railStationArrival = true;

            let railStationArrivals = [];

            let railStationArrivalIndex = railStationsArrivalIndex[i];
            let lineColorRGBs = [this.battlegroundRailStations[i].arrivals[railStationsArrivalIndex[i]].colorRGB];
            let lineDashes = [this.battlegroundRailStations[i].arrivals[railStationsArrivalIndex[i]].lineDash];

            railStationsArrivalIndex[i]++;

            if (railStationsArrivalIndex[i] < this.battlegroundRailStations[i].arrivals.length) {
              if (Math.round(this.battlegroundRailStations[i].arrivals[railStationsArrivalIndex[i]].time) === Math.round(this.battlegroundRailStations[i].arrivals[railStationArrivalIndex].time)) {
                lineColorRGBs.push(this.battlegroundRailStations[i].arrivals[railStationsArrivalIndex[i]].colorRGB);
                lineDashes.push(this.battlegroundRailStations[i].arrivals[railStationsArrivalIndex[i]].lineDash);

                railStationsArrivalIndex[i]++;

                if (railStationsArrivalIndex[i] < this.battlegroundRailStations[i].arrivals.length) {
                  if (Math.round(this.battlegroundRailStations[i].arrivals[railStationsArrivalIndex[i]].time) === Math.round(this.battlegroundRailStations[i].arrivals[railStationArrivalIndex].time)) {
                    lineColorRGBs.push(this.battlegroundRailStations[i].arrivals[railStationsArrivalIndex[i]].colorRGB);
                    lineDashes.push(this.battlegroundRailStations[i].arrivals[railStationsArrivalIndex[i]].lineDash);

                    railStationsArrivalIndex[i]++;
                  }
                }
              } else if ((this.battlegroundRailStations[i].arrivals[railStationsArrivalIndex[i]].time <= (nextArrivalTime + 10)) &&
                         (this.battlegroundRailStations[i].arrivals[railStationsArrivalIndex[i]].phaseNumber === nextArrivalPhaseNumber)) {
                if ((i % 2) === 0) {
                  railStationArrivals.push(<span className="setTime" onClick={() => this.setTime(this.battlegroundRailStations[i].arrivals[railStationArrivalIndex].time)}><ColorLineCanvas width="33" height={emMeasurement} backgroundColor='#535355' lineColorRGBs={lineColorRGBs} lineDashes={lineDashes} /> {this.convertSecondsToPhaseString(battlegroundName, Math.round(this.battlegroundRailStations[i].arrivals[railStationArrivalIndex].time))}</span>);
                } else {
                  railStationArrivals.push(<span className="setTime" onClick={() => this.setTime(this.battlegroundRailStations[i].arrivals[railStationArrivalIndex].time)}><ColorLineCanvas width="33" height={emMeasurement} backgroundColor='#2d2d2f' lineColorRGBs={lineColorRGBs} lineDashes={lineDashes} /> {this.convertSecondsToPhaseString(battlegroundName, Math.round(this.battlegroundRailStations[i].arrivals[railStationArrivalIndex].time))}</span>);
                }

                railStationArrivalIndex = railStationsArrivalIndex[i];
                lineColorRGBs = [this.battlegroundRailStations[i].arrivals[railStationsArrivalIndex[i]].colorRGB];
                lineDashes = [this.battlegroundRailStations[i].arrivals[railStationsArrivalIndex[i]].lineDash];

                railStationsArrivalIndex[i]++;
              }
            }

            if (railStationArrivals.length > 0) { railStationArrivals.push(<React.Fragment> </React.Fragment>); }

            if ((i % 2) === 0) {
              railStationArrivals.push(<span className="setTime" onClick={() => this.setTime(this.battlegroundRailStations[i].arrivals[railStationArrivalIndex].time)}><ColorLineCanvas width="33" height={emMeasurement} backgroundColor='#535355' lineColorRGBs={lineColorRGBs} lineDashes={lineDashes} /> {this.convertSecondsToPhaseString(battlegroundName, Math.round(this.battlegroundRailStations[i].arrivals[railStationArrivalIndex].time))}</span>);

              if (newPhase) {
                timeTableTableRowCells[ii].push(<td className="scrollable alternateBackground firstInPhase">{railStationArrivals}</td>);
              } else {
                timeTableTableRowCells[ii].push(<td className="scrollable alternateBackground">{railStationArrivals}</td>);
              }
            } else {
              railStationArrivals.push(<span className="setTime" onClick={() => this.setTime(this.battlegroundRailStations[i].arrivals[railStationArrivalIndex].time)}><ColorLineCanvas width="33" height={emMeasurement} backgroundColor='#2d2d2f' lineColorRGBs={lineColorRGBs} lineDashes={lineDashes} /> {this.convertSecondsToPhaseString(battlegroundName, Math.round(this.battlegroundRailStations[i].arrivals[railStationArrivalIndex].time))}</span>);

              if (newPhase) {
                timeTableTableRowCells[ii].push(<td className="scrollable firstInPhase">{railStationArrivals}</td>);
              } else {
                timeTableTableRowCells[ii].push(<td className="scrollable">{railStationArrivals}</td>);
              }
            }
          }
        }

        if (!railStationArrival) {
          if ((i % 2) === 0) {
            if (newPhase) {
              timeTableTableRowCells[ii].push(<td className="scrollable alternateBackground firstInPhase"></td>);
            } else {
              timeTableTableRowCells[ii].push(<td className="scrollable alternateBackground"></td>);
            }
          } else {
            if (newPhase) {
              timeTableTableRowCells[ii].push(<td className="scrollable firstInPhase"></td>);
            } else {
              timeTableTableRowCells[ii].push(<td className="scrollable"></td>);
            }
          }
        }
      }

      anyRailStationIncomplete = false;

      for (let i = 0; i < this.battlegroundRailStations.length; i++) {
        anyRailStationIncomplete = anyRailStationIncomplete || (railStationsArrivalIndex[i] < this.battlegroundRailStations[i].arrivals.length);
      }

      newPhase = false;
    }

    if (phaseNumberNumberOfColumns > 0) {
      let phaseTime = 0;

      for (let i = 0; i < (phaseNumber - 1); i++) {
        phaseTime += this.battlegrounds[battlegroundName].phasesDefaultValues[i].delay +
                     this.battlegrounds[battlegroundName].phasesDefaultValues[i].wait +
                     this.battlegrounds[battlegroundName].phasesDefaultValues[i].move;
      }

      timeTableTableRowCells[0].push(<td className="scrollable firstInPhase" colspan={phaseNumberNumberOfColumns}><span className="setTime" onClick={() => this.setTime(phaseTime)}>Phase {phaseNumber}</span></td>);
    }

    timeTableTableRows.push(<tr>{timeTableTableRowCells[0]}</tr>);

    for (let i = 0; i < this.battlegroundRailStations.length; i++) {
      let ii = i + 1;

      timeTableTableRows.push(<tr>{timeTableTableRowCells[ii]}</tr>);
    }

    const timeTableTable = <table>
                             <tbody>
                               {timeTableTableRows}
                             </tbody>
                           </table>;

    const timeTableTableDivMarginLeft = timeTableTableHeaderMeasurementDiv.clientWidth + 0.8 * emMeasurement + 2;

    let timeTableTableDivWidth = 4.4 * emMeasurement;

    if (this.title.current != null) {
      timeTableTableDivWidth += this.title.current.clientWidth - timeTableTableDivMarginLeft;

      if (this.credits.current != null) {
        timeTableTableDivWidth -= this.credits.current.clientWidth;
      }

      timeTableTableDivWidth -= 1;
    }

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    let allowedRight = document.documentElement.clientWidth;
    if (this.minimapSpacer.current != null) { allowedRight -= this.minimapSpacer.current.clientWidth; }

    let tooltipDivLeft = this.clientMouseX + 14;

    const tooltipMeasurementDiv = document.getElementById('tooltipMeasurement');

    if ((tooltipDivLeft + tooltipMeasurementDiv.clientWidth) > allowedRight) {
      tooltipDivLeft = allowedRight - tooltipMeasurementDiv.clientWidth;
    }

    const tooltipDiv = <div className="tooltip" style={{ display : this.minimapMouseHover && (this.tooltipLines.length > 0) ? 'table' : 'none', visibility : 'visible', position : 'fixed', left : tooltipDivLeft, top : this.clientMouseY + 20 }}>
                         <table>
                           <tbody>
                             {this.tooltipLines}
                           </tbody>
                         </table>
                       </div>;

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    return (
      <React.Fragment>
        <div className="minimap">
          <div className="minimapSpacer" ref={this.minimapSpacer}></div>
          <canvas className="minimap" width="254" height="254" style={{ touchAction : 'none' }} onMouseEnter={this.mouseEnterMinimap} onMouseLeave={this.mouseLeaveMinimap} onMouseMove={this.mouseMoveMinimap} onTouchEnd={this.touchEnd} onTouchMove={this.touchMove} onTouchStart={this.touchStart} onWheel={this.scaleMinimap} ref={this.minimapCanvas} />
          <div>
            <div style={{ width : timeInputWidth }} className="time">{timeInput}{timeInputTicks}</div>
            {timeDiv}
            {playPauseInput}
            {playSpeedSelect}
            {loadingNotificationDiv}
          </div>
        </div>
        <div className="settings" ref={this.settings}>
          <Menu width="25.0em">
            <img className="menuIcon" src="/shortcut_icon.png" alt="" /><a className="menu-item menuLink" href={lbzeHREF}><span>Lunchmeat's Blue Zone Elucidator</span></a>
            <img className="menuIcon" src="/oaf/shortcut_icon.png" alt="" /><a className="menu-item menuLink" href={loafbzeHREF}><span>Lunchmeat's Once & Future Blue Zone Elucidator</span></a>
            <img className="menuIcon" src="/mts/shortcut_icon.png" alt="" /><a className="menu-item menuLink" href={lmtsHREF}><span>Lunchmeat's Moving Transit Schedule</span></a>
          </Menu>
          <div className="titles">
            <div className="title" ref={this.title}>Lunchmeat's Moving Transit Schedule</div>
            <div className="subtitle">with game data from Updates 7.1 – 29.1</div>
          </div>
          <div className="credits" ref={this.credits}>
            <figure>
              <a href="https://www.jonmarchione.com/"><img src="/images/lunchmeat.webp" title="Lunchmeat art by Jon Marchione" alt="Lunchmeat in a Tactical Stance with an Assault Rifle" width="140" height="146" /></a>
              <figcaption><br/>LMTS is created by <br/><a href="mailto:andymandias@lunchmeat.zone">andymandias</a><br/> with extensive help from <br/><a href="https://twitter.com/ASJ_sapphics">sapphics</a><br/> who mined all of the game data</figcaption>
            </figure>
          </div>
          <div className="battleground">
            <select onChange={this.selectBattleground} ref={this.battleground}>
              <option value="Erangel" selected>Erangel</option>
              <option value="Vikendi_20.2">Vikendi (Remastered)</option>
              <option value="Vikendi">Vikendi (Reborn)</option>
            </select>
          </div>
          <br/>
          <div className="scrollable" style={{ display : this.battlegroundRailStations.length > 0 ? 'block' : 'none', marginLeft : railsInformationTableDivMarginLeft, width : railsInformationTableDivWidth }}>
            {railsInformationTable}
          </div>
          <br/>
          <div className="scrollable" style={{ display : this.battlegroundRailStations.length > 0 ? 'block' : 'none', marginLeft : timeTableTableDivMarginLeft, width : timeTableTableDivWidth }}>
            {timeTableTable}
          </div>
          {phasesTable}
        </div>
        <ReactTooltip className="infoTooltip" place="top" effect="solid" backgroundColor="#535355" textColor="#fefeff" border={true} borderColor="#2d2d2f" offset={{ left : -1 }} globalEventOff="click"/>
        {tooltipDiv}
        <canvas width="254" height="254" className="hidden" ref={this.highResolutionMinimapCanvas} />
      </React.Fragment>
    );
  }
}

// --------------------------------------------------------------------

class Phase extends React.Component {
  constructor(props) {
    super(props);
    this.delay = React.createRef();
    this.wait = React.createRef();
    this.move = React.createRef();
    this.dps = React.createRef();
    this.shrink = React.createRef();
    this.spread = React.createRef();
    this.landRatio = React.createRef();
  }

  render() {
    return (
      <tr>
        <td className="header">Phase {this.props.number}</td>
        <td className="phases"><input type="number" step="1" onChange={this.props.delayOnChange} ref={this.delay} /></td>
        <td className="phases"><input type="number" step="1" onChange={this.props.waitOnChange} ref={this.wait} /></td>
        <td className="phases"><input type="number" step="1" onChange={this.props.moveOnChange} ref={this.move} /></td>
        <td className="phases"><input type="number" step="0.1" onChange={this.props.dpsOnChange} ref={this.dps} /></td>
        <td className="phases"><input type="number" className="geometric" step="0.01" onChange={this.props.shrinkOnChange} ref={this.shrink} /></td>
        <td className="phases"><input type="number" step="0.01" onChange={this.props.spreadOnChange} ref={this.spread} /></td>
        <td className="phases"><input type="number" step="0.01" onChange={this.props.landRatioOnChange} ref={this.landRatio} /></td>
      </tr>
    );
  }
}

// --------------------------------------------------------------------

class PhaseDefaultValues {
  constructor(delay, wait, move, dps, shrink, spread, landRatio) {
    this.delay = delay;
    this.wait = wait;
    this.move = move;
    this.dps = dps;
    this.shrink = shrink;
    this.spread = spread;
    this.landRatio = landRatio;
  }
}

// --------------------------------------------------------------------

class Rail {
  constructor(battleground, colorRGB, lineDash, railNodes, railVehicles, railStations) {
    this.battleground = battleground;
    this.colorRGB = colorRGB;
    this.lineDash = lineDash;
    this.railNodes = railNodes;
    this.railPath = new Path2D();
    this.railStations = railStations;
    this.railStationsPath = new Path2D();
    this.railVehicles = railVehicles;
    this.railVehiclesClipPath = new Path2D();
    this.railVehiclesPath = new Path2D();
    this.setDuration();
    this.setLength();
  }

  getCoordinate(index, time, offsetDistance, loop, inverseMove) {
    const distanceTolerance = 0.1;

    let distance = this.railNodes[index].moveCurve.getValue(time) + offsetDistance;

    if (distance < (this.railNodes[index].reparameterizationTable.minimumCoordinate() - distanceTolerance)) {
      distance -= this.railNodes[index].reparameterizationTable.minimumCoordinate();

      if (inverseMove) {
        if (index !== (this.railNodes.length - 1)) {
          const offsetIndex = (index + 1) % this.railNodes.length;

          distance += this.railNodes[offsetIndex].reparameterizationTable.maximumCoordinate();

          const t = this.railNodes[offsetIndex].reparameterizationTable.getValue(distance);

          return this.railNodes[offsetIndex].getCoordinate(t);
        } else {
          const t = this.railNodes[index].reparameterizationTable.getValue(this.railNodes[index].reparameterizationTable.maximumCoordinate() - distance);

          return this.railNodes[index].getCoordinate(t);
        }
      } else if (loop || (index !== 0)) {
        const offsetIndex = (index + this.railNodes.length - 1) % this.railNodes.length;

        distance += this.railNodes[offsetIndex].reparameterizationTable.maximumCoordinate();

        const t = this.railNodes[offsetIndex].reparameterizationTable.getValue(distance);

        return this.railNodes[offsetIndex].getCoordinate(t);
      } else {
        const t = this.railNodes[index].reparameterizationTable.getValue(this.railNodes[index].reparameterizationTable.minimumCoordinate() - distance);

        return this.railNodes[index].getCoordinate(t);
      }
    } else if (distance > (this.railNodes[index].reparameterizationTable.maximumCoordinate() + distanceTolerance)) {
      distance -= this.railNodes[index].reparameterizationTable.maximumCoordinate();

      if (inverseMove) {
        if (index !== 0) {
          const offsetIndex = (index + this.railNodes.length - 1) % this.railNodes.length;

          distance += this.railNodes[offsetIndex].reparameterizationTable.minimumCoordinate();

          const t = this.railNodes[offsetIndex].reparameterizationTable.getValue(distance);

          return this.railNodes[offsetIndex].getCoordinate(t);
        } else {
          const t = this.railNodes[index].reparameterizationTable.getValue(this.railNodes[index].reparameterizationTable.minimumCoordinate() - distance);

          return this.railNodes[index].getCoordinate(t);
        }
      } else if (loop || (index !== (this.railNodes.length - 1))) {
        const offsetIndex = (index + 1) % this.railNodes.length;

        distance += this.railNodes[offsetIndex].reparameterizationTable.minimumCoordinate();

        const t = this.railNodes[offsetIndex].reparameterizationTable.getValue(distance);

        return this.railNodes[offsetIndex].getCoordinate(t);
      } else {
        const t = this.railNodes[index].reparameterizationTable.getValue(this.railNodes[index].reparameterizationTable.maximumCoordinate() - distance);

        return this.railNodes[index].getCoordinate(t);
      }
    } else {
      const t = this.railNodes[index].reparameterizationTable.getValue(distance);

      return this.railNodes[index].getCoordinate(t);
    }
  }

  setDuration() {
    this.duration = 0;

    for (let i = 0; i < this.railNodes.length; i++) {
      this.duration += this.railNodes[i].duration;
    }
  }

  setLength() {
    this.length = 0;

    for (let i = 0; i < this.railNodes.length; i++) {
      this.length += this.railNodes[i].length;
    }
  }

  setRailPath(matrix, scale, unitLength) {
    this.railPath = new Path2D();

    this.railStationsPath = new Path2D();

    const railStationTickLength = Math.max(30.0 * 2080.0 / (scale * unitLength), 30.0 / unitLength);

    const railLoop = this.railVehicles[0].loop;

    let railStationDistance,
        railStationVectorEndDistance;

    for (let i = 0; i < this.railNodes.length; ++i) {
      this.railPath.addPath(this.railNodes[i].path, matrix);

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

      if (this.railNodes[i].inverseMove || railLoop) {
        railStationDistance = this.railNodes[i].reparameterizationTable.maximumCoordinate();

        if (railLoop) {
          railStationDistance -= 0.5 * this.railVehicles[0].length;
        }
      } else {
        railStationDistance = 0.0;
      }

      let railStationCoordinate = this.railNodes[i].getCoordinate(this.railNodes[i].reparameterizationTable.getValue(railStationDistance));

      if (this.railNodes[i].inverseMove || railLoop) {
        railStationVectorEndDistance = railStationDistance - 1.0;
      } else {
        railStationVectorEndDistance = railStationDistance + 1.0;
      }

      let railStationVectorEndCoordinate = this.railNodes[i].getCoordinate(this.railNodes[i].reparameterizationTable.getValue(railStationVectorEndDistance));

      let railStationVector = [railStationVectorEndCoordinate[0] - railStationCoordinate[0], railStationVectorEndCoordinate[1] - railStationCoordinate[1]];

      let railStationTickAngle = Math.atan2(railStationVector[1], railStationVector[0]) + 0.5 * Math.PI;

      let railStationTickPath = new Path2D();

      railStationTickPath.moveTo(railStationCoordinate[0] - 0.5 * railStationTickLength * Math.cos(railStationTickAngle), railStationCoordinate[1] - 0.5 * railStationTickLength * Math.sin(railStationTickAngle));

      railStationTickPath.lineTo(railStationCoordinate[0] + 0.5 * railStationTickLength * Math.cos(railStationTickAngle), railStationCoordinate[1] + 0.5 * railStationTickLength * Math.sin(railStationTickAngle));

      this.railStationsPath.addPath(railStationTickPath, matrix);

//--  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --

      if ((i === (this.railNodes.length - 1)) && !railLoop) {
        if (this.railNodes[i].inverseMove) {
          railStationDistance = 0.0;
        } else {
          railStationDistance = this.railNodes[i].reparameterizationTable.maximumCoordinate();
        }

        railStationCoordinate = this.railNodes[i].getCoordinate(this.railNodes[i].reparameterizationTable.getValue(railStationDistance));

        if (this.railNodes[i].inverseMove) {
          railStationVectorEndDistance = railStationDistance + 1.0;
        } else {
          railStationVectorEndDistance = railStationDistance - 1.0;
        }

        railStationVectorEndCoordinate = this.railNodes[i].getCoordinate(this.railNodes[i].reparameterizationTable.getValue(railStationVectorEndDistance));

        railStationVector = [railStationVectorEndCoordinate[0] - railStationCoordinate[0], railStationVectorEndCoordinate[1] - railStationCoordinate[1]];

        railStationTickAngle = Math.atan2(railStationVector[1], railStationVector[0]) + 0.5 * Math.PI;

        railStationTickPath = new Path2D();

        railStationTickPath.moveTo(railStationCoordinate[0] - 0.5 * railStationTickLength * Math.cos(railStationTickAngle), railStationCoordinate[1] - 0.5 * railStationTickLength * Math.sin(railStationTickAngle));

        railStationTickPath.lineTo(railStationCoordinate[0] + 0.5 * railStationTickLength * Math.cos(railStationTickAngle), railStationCoordinate[1] + 0.5 * railStationTickLength * Math.sin(railStationTickAngle));

        this.railStationsPath.addPath(railStationTickPath, matrix);
      }
    }
  }

  setRailVehiclesPaths(matrix, scale, unitLength, time) {
    this.railVehiclesPath = new Path2D();

    this.railVehiclesClipPath = new Path2D();

    for (let i = 0; i < this.railVehicles.length; ++i) {
      let railVehicleTime = time;

      let railVehicleIndex = this.railVehicles[i].startingIndex;

      let nextRailVehicleIndex;

      let railVehicleReverse = false;

      while (railVehicleTime >= 0.0) {
        railVehicleReverse = false;

        for (let j = 0; j < (2 * this.railNodes.length); ++j) {
          let jj = this.railVehicles[i].startingIndex + j;

          if (jj >= this.railNodes.length) {
            jj = jj % this.railNodes.length;

            if (!this.railVehicles[i].loop) {
              jj = this.railNodes.length - 1 - jj;

              if (jj >= 0) {
                railVehicleReverse = true;
              } else {
                jj = -(jj + 1);

                railVehicleReverse = false;
              }
            }
          }

          railVehicleTime -= this.railNodes[jj].duration;

          if (railVehicleTime < 0.0) {
            railVehicleIndex = jj;

            jj = this.railVehicles[i].startingIndex + j + 1;

            if (jj >= this.railNodes.length) {
              jj = jj % this.railNodes.length;

              if (!this.railVehicles[i].loop) {
                jj = this.railNodes.length - 1 - jj;

                if (jj < 0) {
                  jj = -(jj + 1);
                }
              }
            }

            nextRailVehicleIndex = jj;

            break;
          }
        }
      }

      railVehicleTime += this.railNodes[railVehicleIndex].duration;

      if (railVehicleReverse) { railVehicleTime = this.railNodes[railVehicleIndex].duration - railVehicleTime; }

      if (this.railNodes[railVehicleIndex].inverseMove) { railVehicleTime = this.railNodes[railVehicleIndex].duration - railVehicleTime; }

      let nextRailStationIndex;

      let nextNextRailStationIndex;

      if (railVehicleReverse !== this.railNodes[railVehicleIndex].inverseMove) {
        if (this.railNodes[railVehicleIndex].inverseMove) {
          nextRailStationIndex = railVehicleIndex + 1;

          if (nextRailStationIndex === this.railNodes.length) {
            nextNextRailStationIndex = railVehicleIndex;
          } else {
            nextNextRailStationIndex = railVehicleIndex + 2;
          }
        } else {
          nextRailStationIndex = railVehicleIndex;

          if (nextRailStationIndex === 0) {
            nextNextRailStationIndex = railVehicleIndex;
          } else {
            nextNextRailStationIndex = railVehicleIndex - 1;
          }
        }

        this.railVehicles[i].timeToDeparture = railVehicleTime - this.railNodes[railVehicleIndex].duration + this.railNodes[railVehicleIndex].departureTime;

        this.railVehicles[i].timeToNextRailStation = railVehicleTime - this.railNodes[railVehicleIndex].duration + this.railNodes[railVehicleIndex].arrivalTime;

        this.railVehicles[i].timeToNextDeparture = railVehicleTime + this.railNodes[nextRailVehicleIndex].departureTime;
      } else {
        if (this.railVehicles[i].loop) {
          nextRailStationIndex = (railVehicleIndex + 1) % this.railNodes.length;

          nextNextRailStationIndex = (nextRailStationIndex + 1) % this.railNodes.length;
        } else if (this.railNodes[railVehicleIndex].inverseMove) {
          nextRailStationIndex = railVehicleIndex;

          if (nextRailStationIndex === 0) {
            nextNextRailStationIndex = railVehicleIndex;
          } else {
            nextNextRailStationIndex = railVehicleIndex - 1;
          }
        } else {
          nextRailStationIndex = railVehicleIndex + 1;

          if (nextRailStationIndex === this.railNodes.length) {
            nextNextRailStationIndex = railVehicleIndex;
          } else {
            nextNextRailStationIndex = railVehicleIndex + 2;
          }
        }

        this.railVehicles[i].timeToDeparture = this.railNodes[railVehicleIndex].departureTime - railVehicleTime;

        this.railVehicles[i].timeToNextRailStation = this.railNodes[railVehicleIndex].arrivalTime - railVehicleTime;

        this.railVehicles[i].timeToNextDeparture = this.railNodes[railVehicleIndex].duration - railVehicleTime + this.railNodes[nextRailVehicleIndex].departureTime;
      }

      this.railVehicles[i].nextRailStation = this.railStations[nextRailStationIndex];

      this.railVehicles[i].nextNextRailStation = this.railStations[nextNextRailStationIndex];

      let locationOffset;

      if (this.railVehicles[i].loop) {
        locationOffset = 0;
      } else {
        let distance = 0;

        if (this.railNodes[railVehicleIndex].inverseMove) {
          for (let j = this.railNodes.length - 1; j > railVehicleIndex; j--) {
            distance += this.railNodes[j].length;
          }
        } else {
          for (let j = 0; j < railVehicleIndex; j++) {
            distance += this.railNodes[j].length;
          }
        }

        distance += this.railNodes[railVehicleIndex].moveCurve.getValue(railVehicleTime);

        if (railVehicleReverse !== this.railNodes[railVehicleIndex].inverseMove) {
          locationOffset = this.railVehicles[i].length * (1.0 - distance / this.length);
        } else {
          locationOffset = this.railVehicles[i].length * distance / this.length;
        }
      }

//--  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --

      let frontRailCarFrontContactCoordinate = [];
      let frontRailCarVector = [];

      let backRailCarBackContactCoordinate = [];
      let backRailCarVector = [];

      let averageRailCarVectorLength = 0.0;

      for (let j = 0; j < this.railVehicles[i].railCars.length; j++) {
        let railCarFrontContactCoordinate = [];
        let railCarBackContactCoordinate = [];

        if ((railVehicleReverse === this.railNodes[railVehicleIndex].inverseMove) && !this.railVehicles[i].loop) {
           railCarFrontContactCoordinate = this.getCoordinate(railVehicleIndex, railVehicleTime, -(this.railVehicles[i].railCars[j].location[0] + locationOffset + this.railVehicles[i].railCars[j].frontContact[0]), this.railVehicles[i].loop, this.railNodes[railVehicleIndex].inverseMove);
           railCarBackContactCoordinate = this.getCoordinate(railVehicleIndex, railVehicleTime, -(this.railVehicles[i].railCars[j].location[0] + locationOffset + this.railVehicles[i].railCars[j].backContact[0]), this.railVehicles[i].loop, this.railNodes[railVehicleIndex].inverseMove);
        } else {
           railCarFrontContactCoordinate = this.getCoordinate(railVehicleIndex, railVehicleTime, (this.railVehicles[i].railCars[j].location[0] + locationOffset + this.railVehicles[i].railCars[j].frontContact[0]), this.railVehicles[i].loop, this.railNodes[railVehicleIndex].inverseMove);
           railCarBackContactCoordinate = this.getCoordinate(railVehicleIndex, railVehicleTime, (this.railVehicles[i].railCars[j].location[0] + locationOffset + this.railVehicles[i].railCars[j].backContact[0]), this.railVehicles[i].loop, this.railNodes[railVehicleIndex].inverseMove);
        }

//--  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --

        if (j === 0) {
          frontRailCarFrontContactCoordinate = railCarFrontContactCoordinate;
        }

        if (j === (this.railVehicles[i].railCars.length - 1)) {
          backRailCarBackContactCoordinate = railCarBackContactCoordinate;
        }

        const railCarCenterCoordinate = [0.5 * (railCarFrontContactCoordinate[0] + railCarBackContactCoordinate[0]),
                                         0.5 * (railCarFrontContactCoordinate[1] + railCarBackContactCoordinate[1])];

        const railCarVector = [railCarFrontContactCoordinate[0] - railCarBackContactCoordinate[0],
                               railCarFrontContactCoordinate[1] - railCarBackContactCoordinate[1]];

        if (j === 0) {
          frontRailCarVector = railCarVector;
        }

        if (j === (this.railVehicles[i].railCars.length - 1)) {
          backRailCarVector = railCarVector;
        }

        averageRailCarVectorLength += Math.sqrt(railCarVector[0] ** 2 + railCarVector[1] ** 2);

//--  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --

        let railCarAngle = Math.atan2(railCarVector[1], railCarVector[0]);

        let railCarPath = new Path2D();

        const railCarMatrix = new DOMMatrix([Math.cos(railCarAngle), Math.sin(railCarAngle), -Math.sin(railCarAngle), Math.cos(railCarAngle),
                                             railCarCenterCoordinate[0], railCarCenterCoordinate[1]]);

        railCarPath.addPath(this.railVehicles[i].railCars[j].simpleOutlinePath, railCarMatrix);

        this.railVehiclesPath.addPath(railCarPath, matrix);
      }

      averageRailCarVectorLength /= this.railVehicles[i].railCars.length;

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

      const railVehicleRadius = Math.max(averageRailCarVectorLength, 6.0 / scale);
      const railVehicleClipRadius = 1.25 * railVehicleRadius;

      let railVehicleClipPath = new Path2D();

//--  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --

      let frontRailCarAngle = Math.atan2(frontRailCarVector[1], frontRailCarVector[0]);

      let frontRailCarArcStart = [frontRailCarFrontContactCoordinate[0] + railVehicleClipRadius * Math.cos(-0.5 * Math.PI + frontRailCarAngle),
                                  frontRailCarFrontContactCoordinate[1] + railVehicleClipRadius * Math.sin(-0.5 * Math.PI + frontRailCarAngle)];

      let frontRailCarArcEnd = [frontRailCarFrontContactCoordinate[0] + railVehicleClipRadius * Math.cos(0.5 * Math.PI + frontRailCarAngle),
                                frontRailCarFrontContactCoordinate[1] + railVehicleClipRadius * Math.sin(0.5 * Math.PI + frontRailCarAngle)];

//--  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --

      let backRailCarAngle = Math.atan2(backRailCarVector[1], backRailCarVector[0]);

      let backRailCarArcStart = [backRailCarBackContactCoordinate[0] + railVehicleClipRadius * Math.cos(0.5 * Math.PI + backRailCarAngle),
                                 backRailCarBackContactCoordinate[1] + railVehicleClipRadius * Math.sin(0.5 * Math.PI + backRailCarAngle)];

      let backRailCarArcEnd = [backRailCarBackContactCoordinate[0] + railVehicleClipRadius * Math.cos(1.5 * Math.PI + backRailCarAngle),
                               backRailCarBackContactCoordinate[1] + railVehicleClipRadius * Math.sin(1.5 * Math.PI + backRailCarAngle)];

//--  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --

      railVehicleClipPath.moveTo(frontRailCarArcStart[0], frontRailCarArcStart[1]);

      railVehicleClipPath.arc(frontRailCarFrontContactCoordinate[0], frontRailCarFrontContactCoordinate[1], railVehicleClipRadius, -0.5 * Math.PI + frontRailCarAngle, 0.5 * Math.PI + frontRailCarAngle);

      let bezierChordLength = Math.sqrt((frontRailCarArcEnd[0] - backRailCarArcStart[0]) ** 2 + (frontRailCarArcEnd[1] - backRailCarArcStart[1]) ** 2);

      let frontRailCarVectorLength = Math.sqrt(frontRailCarVector[0] ** 2 + frontRailCarVector[1] ** 2);

      let frontRailCarTangent = [-frontRailCarVector[0] / frontRailCarVectorLength * bezierChordLength * 1.25, -frontRailCarVector[1] / frontRailCarVectorLength * bezierChordLength * 1.25];

      let backRailCarVectorLength = Math.sqrt(backRailCarVector[0] ** 2 + backRailCarVector[1] ** 2);

      let backRailCarTangent = [-backRailCarVector[0] / backRailCarVectorLength * bezierChordLength * 1.25, -backRailCarVector[1] / backRailCarVectorLength * bezierChordLength * 1.25];

      let controlCoordinates = [[frontRailCarArcEnd[0] + frontRailCarTangent[0] / 3.0, frontRailCarArcEnd[1] + frontRailCarTangent[1] / 3.0],
                                [backRailCarArcStart[0] - backRailCarTangent[0] / 3.0, backRailCarArcStart[1] - backRailCarTangent[1] / 3.0]];

      railVehicleClipPath.bezierCurveTo(controlCoordinates[0][0], controlCoordinates[0][1],
                                        controlCoordinates[1][0], controlCoordinates[1][1],
                                        backRailCarArcStart[0], backRailCarArcStart[1]);

      railVehicleClipPath.arc(backRailCarBackContactCoordinate[0], backRailCarBackContactCoordinate[1], railVehicleClipRadius, 0.5 * Math.PI + backRailCarAngle, 1.5 * Math.PI + backRailCarAngle);

      bezierChordLength = Math.sqrt((backRailCarArcEnd[0] - frontRailCarArcStart[0]) ** 2 + (backRailCarArcEnd[1] - frontRailCarArcStart[1]) ** 2);

      frontRailCarTangent = [frontRailCarVector[0] / frontRailCarVectorLength * bezierChordLength * 1.25, frontRailCarVector[1] / frontRailCarVectorLength * bezierChordLength * 1.25];

      backRailCarTangent = [backRailCarVector[0] / backRailCarVectorLength * bezierChordLength * 1.25, backRailCarVector[1] / backRailCarVectorLength * bezierChordLength * 1.25];

      controlCoordinates = [[backRailCarArcEnd[0] + backRailCarTangent[0] / 3.0, backRailCarArcEnd[1] + backRailCarTangent[1] / 3.0],
                            [frontRailCarArcStart[0] - frontRailCarTangent[0] / 3.0, frontRailCarArcStart[1] - frontRailCarTangent[1] / 3.0]];

      railVehicleClipPath.bezierCurveTo(controlCoordinates[0][0], controlCoordinates[0][1],
                                        controlCoordinates[1][0], controlCoordinates[1][1],
                                        frontRailCarArcStart[0], frontRailCarArcStart[1]);

      railVehicleClipPath.closePath();

//--  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --

      this.railVehiclesClipPath.addPath(railVehicleClipPath, matrix);

      this.railVehicles[i].clipPath = new Path2D();

      this.railVehicles[i].clipPath.addPath(railVehicleClipPath, matrix);
    }
  }
}

// --------------------------------------------------------------------

class RailCar {
  constructor(location, frontContact, backContact, simpleOutlinePath) {
    this.location = location;
    this.frontContact = frontContact;
    this.backContact = backContact;
    this.simpleOutlinePath = simpleOutlinePath;
  }
}

// --------------------------------------------------------------------

class RailCurve {
  constructor(leavePosition, arrivePosition) {
    this.startCoordinate = leavePosition;
    this.endCoordinate = arrivePosition;
  }
}

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

class BezierCurve extends RailCurve {
  constructor(leavePosition, leaveTangent, arriveTangent, arrivePosition) {
    super(leavePosition, arrivePosition);

    this.controlCoordinates = [[leavePosition[0] + leaveTangent[0] / 3.0, leavePosition[1] + leaveTangent[1] / 3.0],
                               [arrivePosition[0] - arriveTangent[0] / 3.0, arrivePosition[1] - arriveTangent[1] / 3.0]];
  }

  addCurveToPath(path) {
    path.bezierCurveTo(this.controlCoordinates[0][0], this.controlCoordinates[0][1],
                       this.controlCoordinates[1][0], this.controlCoordinates[1][1],
                       this.endCoordinate[0], this.endCoordinate[1]);
  }

  getCoordinate (t) {
    return [this.startCoordinate[0] * (1.0 - t) ** 3 +
            3.0 * this.controlCoordinates[0][0] * (1.0 - t) ** 2 * t +
            3.0 * this.controlCoordinates[1][0] * (1.0 - t) * t ** 2 +
            this.endCoordinate[0] * t ** 3,
            this.startCoordinate[1] * (1.0 - t) ** 3 +
            3.0 * this.controlCoordinates[0][1] * (1.0 - t) ** 2 * t +
            3.0 * this.controlCoordinates[1][1] * (1.0 - t) * t ** 2 +
            this.endCoordinate[1] * t ** 3];
  }
}

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

class LinearCurve extends RailCurve {
  constructor(leavePosition, arrivePosition) {
    super(leavePosition, arrivePosition);

    this.setLength();
  }

  addCurveToPath(path) {
    path.lineTo(this.endCoordinate[0], this.endCoordinate[1]);
  }

  getCoordinate (t) {
    return [this.startCoordinate[0] * (1.0 - t) +
            this.endCoordinate[0] * t,
            this.startCoordinate[1] * (1.0 - t) +
            this.endCoordinate[1] * t];
  }

  getDistance(startCoordinate, endCoordinate) {
    return Math.sqrt((endCoordinate[0] - startCoordinate[0]) ** 2 + (endCoordinate[1] - startCoordinate[1]) ** 2);
  }

  setLength() {
    this.length = this.getDistance(this.startCoordinate, this.endCoordinate);
  }
}

// --------------------------------------------------------------------

class RailNode {
  constructor(startCoordinate, railCurves, reparameterizationTable, moveCurve, inverseMove) {
    this.startCoordinate = startCoordinate;
    this.railCurves = railCurves;
    this.reparameterizationTable = reparameterizationTable;
    this.moveCurve = moveCurve;
    this.inverseMove = inverseMove;
    this.setDuration();
    this.setDepartureTime();
    this.setArrivalTime();
    this.setLength();
    this.setPath();
  }

  convertTimeToT(time) {
    const distance = this.moveCurve.getValue(time);

    return this.reparameterizationTable.getValue(distance);
  }

  getCoordinate(t) {
    const index = Math.min(Math.trunc(t), this.railCurves.length - 1);

    const railCurveT = t - index;

    return this.railCurves[index].getCoordinate(railCurveT);
  }

  setArrivalTime() {
    const numberOfInterpolationRanges = this.moveCurve.interpolationRanges.length;

    if (this.moveCurve.interpolationRanges[numberOfInterpolationRanges-2].endValue === this.moveCurve.interpolationRanges[numberOfInterpolationRanges-1].endValue) {
      this.arrivalTime = this.moveCurve.interpolationRanges[numberOfInterpolationRanges-2].endCoordinate;
    } else {
      this.arrivalTime = this.moveCurve.interpolationRanges[numberOfInterpolationRanges-1].endCoordinate;
    }
  }

  setDepartureTime() {
    if (this.moveCurve.interpolationRanges[1].endValue === this.moveCurve.interpolationRanges[0].endValue) {
      this.departureTime = this.moveCurve.interpolationRanges[1].endCoordinate;
    } else {
      this.departureTime = this.moveCurve.interpolationRanges[0].endCoordinate;
    }
  }

  setDuration() {
    const numberOfInterpolationRanges = this.moveCurve.interpolationRanges.length;

    this.duration = this.moveCurve.interpolationRanges[numberOfInterpolationRanges-1].endCoordinate;
  }

  setLength() {
    this.length = this.reparameterizationTable.maximumCoordinate() -
                  this.reparameterizationTable.minimumCoordinate();
  }

  setPath() {
    this.path = new Path2D();

    this.path.moveTo(this.startCoordinate[0], this.startCoordinate[1]);

    for (let i = 0; i < this.railCurves.length; i++) {
      this.railCurves[i].addCurveToPath(this.path);
    }
  }
}

// --------------------------------------------------------------------

class RailStation {
  constructor(battleground, name) {
    this.arrivals = [];
    this.battleground = battleground;
    this.name = name;
  }
}

// --------------------------------------------------------------------

class RailStationArrival {
  constructor(phaseNumber, time, colorRGB, lineDash) {
    this.colorRGB = colorRGB;
    this.lineDash = lineDash;
    this.phaseNumber = phaseNumber;
    this.time = time;
  }
}

// --------------------------------------------------------------------

class RailVehicle {
  constructor(startingIndex, loop, railCars) {
    this.clipPath = new Path2D();
    this.startingIndex = startingIndex;
    this.loop = loop;
    this.nextNextRailStation = "";
    this.nextRailStation = "";
    this.railCars = railCars;
    this.timeToDeparture = 0;
    this.timeToNextRailStation = 0;
    this.setLength();
  }

  setLength() {
    this.length = -(this.railCars[this.railCars.length-1].location[0] + this.railCars[this.railCars.length-1].backContact[0]);
  }
}

// --------------------------------------------------------------------

class TooltipLine extends React.Component {
  render() {
    const emMeasurement = document.getElementById('emMeasurement').clientHeight;

    return (
      <tr>
        <td className="tooltipLine"><ColorLineCanvas width="33" height={emMeasurement} backgroundColor='#535355' lineColorRGBs={[this.props.lineColorRGB]} lineDashes={[this.props.lineDash]} /></td><td className="tooltipLine tooltipLineText">{this.props.text}</td>
      </tr>
    );
  }
}

// ====================================================================

const railsInformationHeaderMeasurementRoot = ReactDOMClient.createRoot(document.getElementById('railsInformationHeaderMeasurement'));

const timeTableTableHeaderMeasurementRoot = ReactDOMClient.createRoot(document.getElementById('timeTableTableHeaderMeasurement'));

const tooltipMeasurementRoot = ReactDOMClient.createRoot(document.getElementById('tooltipMeasurement'));

const root = ReactDOMClient.createRoot(document.getElementById('lmts'));
root.render(<MovingTransitSchedule />);
